import Decimal$1, { Decimal } from "decimal.js";
import { DateTime, Duration, Settings } from "luxon";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
//#region src/date/InvariantDateTime.ts
var invariantDateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
var InvariantDateTime = class extends Date {};
//#endregion
//#region src/enums/CharacterCasing.ts
/**
* Specifies the Character casing.
*/
var CharacterCasing = /* @__PURE__ */ function(CharacterCasing) {
	/** lower casing */
	CharacterCasing["lowerCasing"] = "lower-casing";
	/** UPPER CASING */
	CharacterCasing["upperCasing"] = "upper-casing";
	/** camelCasing */
	CharacterCasing["camelCasing"] = "camel-casing";
	/** PascalCasing */
	CharacterCasing["pascalCasing"] = "pascal-casing";
	/** Normal */
	CharacterCasing["normal"] = "normal";
	return CharacterCasing;
}({});
//#endregion
//#region src/enums/DataType.ts
/**
* Data type.
*/
var DataType = /* @__PURE__ */ function(DataType) {
	/** Auto incremented integer */
	DataType["autoIncrementedInteger"] = "AutoIncrementedInteger";
	/** Boolean */
	DataType["boolean"] = "Boolean";
	/** Color */
	DataType["color"] = "Color";
	/** Data type */
	DataType["dataType"] = "DataType";
	/** Decimal */
	DataType["decimal"] = "Decimal";
	/** File */
	DataType["file"] = "File";
	/** Guid */
	DataType["guid"] = "Guid";
	/** Image */
	DataType["image"] = "Image";
	/** Integer */
	DataType["integer"] = "Integer";
	/** String */
	DataType["string"] = "String";
	/** String array */
	DataType["stringArray"] = "StringArray";
	/** Date */
	DataType["date"] = "Date";
	/** Date/time (UTC)*/
	DataType["dateTime"] = "DateTime";
	DataType["invariantDateTime"] = "InvariantDateTime";
	/** Enumeration */
	DataType["enum"] = "Enum";
	/** Localizable string */
	DataType["localizableString"] = "LocalizableString";
	/** Long */
	DataType["long"] = "Long";
	return DataType;
}({});
function isEnumDataType(dataType) {
	return dataType === "Enum" || dataType === "DataType";
}
function isDateDataType(dataType) {
	return dataType === "Date" || dataType === "DateTime" || dataType === "InvariantDateTime";
}
function getDataTypeIcon(dataType, lookup) {
	if (!dataType || lookup) return "search";
	return `dataType${dataType}`;
}
function getDataTypeDefaultFormat(dataType) {
	switch (dataType) {
		case "Date": return "d";
		case "DateTime":
		case "InvariantDateTime": return "g";
		case "Decimal": return "n6";
		case "Integer":
		case "Long":
		case "AutoIncrementedInteger": return "n0";
		default: return;
	}
}
//#endregion
//#region src/enums/OpenModeType.ts
/**
* Specifies the mode in which the page can be opened.
*
* Please note that there is no direct mapping between the C# abstraction
* and this enum because it is a combination of two enums:
* - NavigationTarget (Frame or Popup)
* - PopupSize (Auto or Full)
*
* This enum also exists as OpenModeType in the UserInterface module of the designer cluster.
*/
var OpenModeType = /* @__PURE__ */ function(OpenModeType) {
	/**
	* New frame.
	*/
	OpenModeType["newFrame"] = "newFrame";
	/**
	* Nested frame.
	*/
	OpenModeType["nestedFrame"] = "nestedFrame";
	/**
	* Same frame.
	*/
	OpenModeType["sameFrame"] = "sameFrame";
	/**
	* Popup (full size).
	*/
	OpenModeType["popupFullSize"] = "popupFullSize";
	/**
	* Popup (autosize)
	*/
	OpenModeType["popupAutoSize"] = "popupAutoSize";
	return OpenModeType;
}({});
//#endregion
//#region src/enums/PopupPosition.ts
/**
* Specifies the popup position.
*
* The C# abstraction put aside,
* this enum also exists as PopupPositionType in the UserInterface module of the designer cluster.
*/
var PopupPosition = /* @__PURE__ */ function(PopupPosition) {
	/**
	* The popup is centred.
	*/
	PopupPosition["center"] = "center";
	/**
	* The popup is on the left.
	*/
	PopupPosition["left"] = "left";
	/**
	* The popup is on the right.
	*/
	PopupPosition["right"] = "right";
	return PopupPosition;
}({});
//#endregion
//#region src/enums/PopupSize.ts
/**
* Specifies the popup size.
*/
var PopupSize = /* @__PURE__ */ function(PopupSize) {
	/**
	* The popup is a full size.
	*/
	PopupSize["full"] = "full";
	/**
	* The popup has an auto size.
	*/
	PopupSize["auto"] = "auto";
	return PopupSize;
}({});
//#endregion
//#region src/enums/WhiteSpaceHandling.ts
/**
* Specifies the white space handling.
*/
var WhiteSpaceHandling = /* @__PURE__ */ function(WhiteSpaceHandling) {
	/** No white space */
	WhiteSpaceHandling["noWhiteSpace"] = "no-white-space";
	/** No trailing white space */
	WhiteSpaceHandling["noTrailingWhiteSpace"] = "no-trailing-white-space";
	/** No leading white space */
	WhiteSpaceHandling["noLeadingWhiteSpace"] = "no-leading-white-space";
	/** No leading and trailing white space */
	WhiteSpaceHandling["noLeadingAndTrailingWhiteSpace"] = "no-leading-and-trailing-white-space";
	return WhiteSpaceHandling;
}({});
//#endregion
//#region src/helpers/GroupedArray.ts
/**
* Represents the grouped array.
*/
var GroupedArray = class {
	/**
	* Initializes a new instance of the GroupedArray class.
	* @param key - Key.
	*/
	constructor(key) {
		this.key = key;
		this.values = [];
	}
	/**
	* Appends new elements to an array, and returns the new length of the array.
	*
	* @param items - New elements of the array.
	*/
	push(...items) {
		return this.values.push(...items);
	}
	map(callbackfn) {
		return this.values.map(callbackfn);
	}
	/**
	* Returns an iterable of values in the array
	*/
	[Symbol.iterator]() {
		return this.values.values();
	}
};
//#endregion
//#region src/helpers/datetime.ts
var dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
var dateTimeWithOptionalTimezoneRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(([+-]\d{2}:\d{2})|Z)?$/;
/**
* That value meant that two-digit years from 00 to 49 translated to 2000-2049.
* Two-digit years from 50 to 99 translated to 1950-1999.
*/
var twoDigitYearMax = 2049;
/**
* A guid whose values is all zeros.
*/
var defaultDatetime = DateTime.utc(1, 1, 1).toJSDate();
/**
* Specifies whether a DateTime object represents a local time, a Coordinated Universal Time (UTC), or is not specified as either local time or UTC.
*/
var DateTimeKind = /* @__PURE__ */ function(DateTimeKind) {
	DateTimeKind[DateTimeKind["Unspecified"] = 0] = "Unspecified";
	DateTimeKind[DateTimeKind["Utc"] = 1] = "Utc";
	DateTimeKind[DateTimeKind["Local"] = 2] = "Local";
	return DateTimeKind;
}({});
/**
* Initializes the default locale to the specified culture or use the navigator culture if not specified.
* @param culture - The culture (ex: en, en-US, fr)
*/
function initializeDefaultLocale(culture) {
	Settings.defaultLocale = culture ?? DateTime.now().resolvedLocaleOptions().locale;
}
/**
* Format a date according to the specified .net format and the specified culture.
* @param date - The date to format.
* @param format - The .net Format
* @param culture - The culture (ex: en, en-US, fr)
*/
function formatDateTime(date, format, culture) {
	if (format === "o" || format === "O") return toIso(date, true);
	const options = culture ? { locale: culture } : void 0;
	return DateTime.fromJSDate(date).toFormat(toLuxonFormat(format), options);
}
/**
* Format a date to the default format according to the specified culture.
* @param date - The date to format.
* @param culture - The culture (ex: en, en-US, fr)
*/
function defaultFormatDateTime(date, culture) {
	const localDate = culture ? DateTime.fromJSDate(date).setLocale(culture) : DateTime.fromJSDate(date);
	return `${localDate.toLocaleString(DateTime.DATE_SHORT)} ${localDate.toLocaleString(DateTime.TIME_WITH_SECONDS)}`;
}
function toLuxonFormat(dotnetFormat) {
	const map = /* @__PURE__ */ new Map([
		["dddd", "cccc"],
		["ddd", "ccc"],
		["dd", "dd"],
		["d", "d"],
		["fff", "SSS"],
		["f", null],
		["F", null],
		["gg", "f"],
		["g", "f"],
		["G", "F"],
		["hh", "hh"],
		["h", "h"],
		["HH", "HH"],
		["H", "H"],
		["mm", "mm"],
		["m", "m"],
		["K", null],
		["MMMM", "LLLL"],
		["MMM", "LLL"],
		["MM", "LL"],
		["M", "L"],
		["ss", "ss"],
		["s", "s"],
		["yyyyy", null],
		["yyyy", "yyyy"],
		["yyy", null],
		["yy", "yy"],
		["y", null],
		["/", "/"],
		["-", "-"],
		[":", ":"],
		["\\.", "."],
		[".", null],
		[" ", " "]
	]);
	if (dotnetFormat === "s") return "yyyy-MM-dd'T'HH:mm:ss";
	const regExp = new RegExp([...map].map((item) => item[0]).join("|"), "g");
	return dotnetFormat.replace(regExp, (m) => {
		const luxonFormat = map.get(m) ?? map.get(`\\${m}`);
		if (luxonFormat == null) throw new Error(`The '${m}' format is not supported.`);
		return luxonFormat;
	});
}
/**
* Create a date according to it's parts.
* @param date - the parts of the date.
*/
function createDateTime(date) {
	if (date.kind === 1) return DateTime.utc(date.year, date.month, date.day, date.hour, date.minute, date.second, date.millisecond ?? 0).toJSDate();
	return DateTime.local(date.year, date.month, date.day, date.hour, date.minute, date.second, date.millisecond ?? 0).toJSDate();
}
/**
* Compares two instances of Date and returns an integer that indicates whether the first instance is earlier than, the same as, or later than the second instance.
* @param date1 - The first date to compare.
* @param date2 - The second date to compare.
*/
function compareDate(date1, date2) {
	const diff = date1.getTime() - date2.getTime();
	if (diff > 0) return 1;
	if (diff < 0) return -1;
	return 0;
}
/** Defines the formatting options that customize string parsing for some date and time parsing methods. */
var DateTimeFormatOption = /* @__PURE__ */ function(DateTimeFormatOption) {
	DateTimeFormatOption[DateTimeFormatOption["None"] = 0] = "None";
	DateTimeFormatOption[DateTimeFormatOption["Local"] = 1] = "Local";
	DateTimeFormatOption[DateTimeFormatOption["Utc"] = 2] = "Utc";
	return DateTimeFormatOption;
}({});
function internalParseDateTime(date, format, culture, option) {
	const options = { zone: "utc" };
	if (culture) options.locale = culture;
	if (option === 1) options.zone = "local";
	if (format) return DateTime.fromFormat(date, toLuxonFormat(format), options);
	return DateTime.fromISO(date, options);
}
/**
* parse a date according to the specified .net format and the specified culture.
* @param date - The date string to parse.
* @param format - The .net Format
* @param culture - The culture (ex: en, en-US, fr)
* @param option - The formatting options */
function parseDateTime(date, format, culture, option) {
	const options = { zone: "utc" };
	if (culture) options.locale = culture;
	if (option === 1) options.zone = "local";
	return internalParseDateTime(date, format, culture, option).toJSDate();
}
/**
* Converts the string representation of a datetime to its date equivalent.
* @param value - A string containing a datetime to convert.
* @param format - The .net Format
* @param result - A datetime equivalent to the string value or null if value is not a date.
* @param culture - The culture (ex: en, en-US, fr)
* @param option - The formatting options
* @returns A value indicating whether the string has been parsed to a datetime with success.
*/
function tryParseDateTime(value, format, result, culture, option) {
	const date = internalParseDateTime(value, format, culture, option);
	if (date.isValid) {
		result.value = date.toJSDate();
		return true;
	}
	result.value = null;
	return false;
}
/**
* Gets the simplified date format according to the specified options.
* @param options - The date time options.
* @returns A string representing the simplified date format.
*/
function getSimplifiedDateFormat(options) {
	return (options.locale === "en-US" ? "MMdd" : "ddMM") + (options.year === "2-digit" ? "yy" : "yyyy");
}
/**
* Converts a simplified string representation of a date to its date equivalent.
* @param value - A string containing a date time to convert.
* @param format - The date format.
* @returns A Date value or null if parsing failed.
*/
function parseSimplifiedDate(value, format) {
	const day = parseDay(value, format);
	if (Number.isNaN(day)) return null;
	const month = parseMonth(value, format);
	if (Number.isNaN(month)) return null;
	const year = parseYear(value, format);
	if (day > new Date(year, month + 1, 0).getDate()) return null;
	const date = new Date(year, month, day);
	if (Number.isNaN(date.getDate())) return null;
	return date;
}
/**
* Adds a specified amount to a given date.
* @param value - The date to which the amount is to be added.
* @param unit - The unit of the amount to be added.
* @param amount - The amount to be added to the date.
* @returns The new date after adding the specified amount.
*/
function addToDate(value, unit, amount) {
	return DateTime.fromJSDate(value).plus({ [unit]: amount }).toJSDate();
}
/**
* Adds the specified number of days to the date value instance.
* @param value - The date value instance.
* @param numberOfDays - A number of whole days. The value parameter can be negative or positive.
* @returns An Date whose value is the sum of the date value and the number of days.
*/
function addDays(value, numberOfDays) {
	return addToDate(value, "days", numberOfDays);
}
/**
* Returns the start of a specified unit for a given date.
* @param value - The date for which to set the start of the unit.
* @param unit - The unit for which to set the start.
* @returns The new date after setting the start of the specified unit.
*/
function startOf(value, unit) {
	return DateTime.fromJSDate(value).startOf(unit).toJSDate();
}
/**
* Returns the end of a specified unit for a given date.
* @param value - The date for which to set the end of the unit.
* @param unit - The unit for which to set the end.
* @returns The new date after setting the end of the specified unit.
*/
function endOf(value, unit) {
	return DateTime.fromJSDate(value).endOf(unit).toJSDate();
}
/**
* Converts the specified year to a four-digit year.
* @param year - A two-digit integer that represents the year to convert.
* @returns An integer that contains the four-digit representation of year.
*/
function toFourDigitYear(year) {
	if (year >= 100) return year;
	return (Math.floor(twoDigitYearMax / 100) - (year > 49 ? 1 : 0)) * 100 + year;
}
function toIso(date, includeOffset) {
	const dateTime = DateTime.fromJSDate(date);
	if (!dateTime.isValid) throw new Error("Invalid date");
	return dateTime.toISO({ includeOffset });
}
function toLocalIso(date) {
	return (/* @__PURE__ */ new Date(`${toIso(date, false)}Z`)).toISOString();
}
function setTimeOfDay(day, time) {
	const date = new Date(day);
	date.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
	return date;
}
function parseDay(value, format) {
	const dayIndex = format.indexOf("dd");
	if (dayIndex === -1) return NaN;
	const day = Number.parseInt(value.substring(dayIndex, dayIndex + 2), 10);
	if (day < 1 || day > 31) return NaN;
	return day;
}
function parseMonth(value, format) {
	const monthIndex = format.indexOf("MM");
	if (monthIndex === -1) return NaN;
	const month = Number.parseInt(value.substring(monthIndex, monthIndex + 2), 10) - 1;
	if (month < 0 || month > 11) return NaN;
	return month;
}
function parseYear(value, format) {
	let yearDigit;
	let yearIndex = format.indexOf("yyyy");
	if (yearIndex === -1) {
		yearDigit = 2;
		yearIndex = format.indexOf("yy");
	} else yearDigit = 4;
	if (yearIndex === -1 || yearIndex >= value.length - 1) return (/* @__PURE__ */ new Date()).getFullYear();
	return toFourDigitYear(Number.parseInt(value.substring(yearIndex, yearIndex + yearDigit), 10));
}
//#endregion
//#region src/helpers/guid.ts
/**
* A guid whose values is all zeros.
*/
var emptyGuid = "00000000-0000-0000-0000-000000000000";
var guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
* Creates a new guid.
*/
function createGuid() {
	const time = Date.now();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replaceAll(/[xy]/g, (c) => {
		const rand = (time + Math.random() * 16) % 16 | 0;
		return (c === "x" ? rand : rand & 3 | 8).toString(16);
	});
}
/**
* Checks if the value is a valid guid.
* @param value - A string value.
* @returns true if the value passed in a guid, false otherwise.
*/
function isGuid(value) {
	return guidRegex.test(value);
}
//#endregion
//#region src/helpers/types.ts
function getDefaultValueByType(type) {
	if (type === "boolean") return false;
	if (type === "number") return 0;
	if (type === "Date") return defaultDatetime;
	if (type === "guid") return emptyGuid;
	return null;
}
/**
* Try to cast a value in the specified type.
* @param value The value to cast
* @param type The expected type
* @returns The casted value or null if the type of the value is not the specified type.
*/
function tryCast(value, type) {
	if (typeof type === "string") switch (type) {
		case "string":
		case "guid": return typeof value === "string" ? value : null;
		case "number": return typeof value === "number" ? value : null;
		case "boolean": return typeof value === "boolean" ? value : null;
		default: return null;
	}
	return value instanceof type ? value : null;
}
/**
* Cast a value in the specified type.
* @param value The value to cast
* @returns The casted value.
*/
function cast(value) {
	return value;
}
/**
* Initialize a target instance with the specified values.
* @param instance The target instance
* @param values The source values
* @returns The instances.
*/
function initialize(instance, values) {
	return Object.assign(instance, values);
}
//#endregion
//#region src/helpers/array.ts
function getArrayElementAt(array, index) {
	if (index < 0 || index >= array.length) throw new Error("Index was out of range. Must be non-negative and less than the size of the array.");
	return array[index];
}
function getArrayElementAtOrDefault(array, type, index) {
	if (index < 0 || index >= array.length) return getDefaultValueByType(type);
	return array[index];
}
function getFirstArrayElement(array, predicate) {
	if (predicate) {
		array = array.filter(predicate);
		if (array.length === 0) throw new Error("Array contains no matching elements.");
	}
	if (array.length === 0) throw new Error("Array contains no elements.");
	return array[0];
}
function getFirstArrayElementOrDefault(array, type, predicate) {
	if (predicate) array = array.filter(predicate);
	if (array.length === 0) return getDefaultValueByType(type);
	return array[0];
}
function getLastArrayElement(array, predicate) {
	if (predicate) {
		array = array.filter(predicate);
		if (array.length === 0) throw new Error("Array contains no matching elements.");
	}
	if (array.length === 0) throw new Error("Array contains no elements.");
	return array[array.length - 1];
}
function getLastArrayElementOrDefault(array, type, predicate) {
	if (predicate) array = array.filter(predicate);
	if (array.length === 0) return getDefaultValueByType(type);
	return array[array.length - 1];
}
function getSingleArrayElement(array, predicate) {
	if (predicate) {
		array = array.filter(predicate);
		if (array.length === 0) throw new Error("Array contains no matching elements.");
		if (array.length > 1) throw new Error("Array contains more than one matching element.");
	}
	if (array.length === 0) throw new Error("Array contains no elements.");
	if (array.length > 1) throw new Error("Array contains more than one element.");
	return array[0];
}
function getSingleArrayElementOrDefault(array, type, predicate) {
	if (predicate) {
		array = array.filter(predicate);
		if (array.length === 0) return getDefaultValueByType(type);
		if (array.length > 1) throw new Error("Array contains more than one matching element.");
	}
	if (array.length === 0) return getDefaultValueByType(type);
	if (array.length > 1) throw new Error("Array contains more than one element.");
	return array[0];
}
function getDefaultArrayIfEmpty(array, type, defaultValue) {
	if (array.length > 0) return array;
	if (defaultValue) return [defaultValue];
	return [getDefaultValueByType(type)];
}
function getNumberArraySum(array) {
	if (array.length === 0) return 0;
	return Decimal$1.sum(...array).toNumber();
}
function getNumberArrayAverage(array) {
	if (array.length === 0) throw new Error("Array contains no elements.");
	return Decimal$1.div(Decimal$1.sum(...array), array.length).toNumber();
}
function groupBy(array, keySelector, elementSelector, keyEquality) {
	return array.reduce((groups, element) => {
		const key = keySelector(element);
		keyEquality ??= (a, b) => a === b;
		let group = groups.find((g) => keyEquality(g.key, key));
		if (!group) {
			group = new GroupedArray(key);
			groups.push(group);
		}
		group.push(elementSelector(element));
		return groups;
	}, []);
}
function isNullArray(array) {
	return !array.some((n) => n !== null && n !== void 0);
}
function compareArray(a, b) {
	return a.length === b.length && a.every((v, i) => JSON.stringify(v) === JSON.stringify(b[i]));
}
/**
* Removes the element at the specified index of the array.
*
* @param array - The array to delete element from.
* @param index - The zero-based index of the element to remove.
*/
function removeAt(array, index) {
	if (index < 0 || index >= array.length) throw new Error("Index was out of range. Must be non-negative and less than the size of the array.");
	array.splice(index, 1);
}
/**
* Removes the first occurrence of a specific object from the array.
*
* @param array - The array to delete element from.
* @param item - The object to remove from the array.
* @returns true if item is successfully removed; otherwise, false. This method also returns false if item was not found in the array.
*/
function remove(array, item) {
	const index = array.indexOf(item);
	if (index !== -1) return array.splice(index, 1).length > 0;
	return false;
}
/**
* Moves item to first position in array if found.
* @param array the array to modify
* @param item the item to move in first position
* @returns return the array in input parameter for fluent entry.
*/
function moveElementFirst(array, item) {
	const itemPosition = array.indexOf(item);
	if (itemPosition > 0) {
		array.splice(itemPosition, 1);
		array.unshift(item);
	}
	return array;
}
/**
* Removes all the elements that match the conditions defined by the specified predicate.
* @param array - The array to delete element from.
* @param predicate - The predicate delegate that defines the conditions of the elements to remove.
* @returns The number of elements removed from the list.
*/
function removeAll(array, predicate) {
	let count = 0;
	for (let i = array.length - 1; i >= 0; i--) if (predicate(array[i])) {
		array.splice(i, 1);
		count++;
	}
	return count;
}
function findLastIndex(array, predicate) {
	for (let i = array.length - 1; i >= 0; i--) if (predicate(array[i])) return i;
	return -1;
}
/**
* Removes all elements from the array.
*/
Array.prototype.clear = function() {
	this.splice(0);
};
//#endregion
//#region src/events/EventManager.ts
var EventManager = class {
	constructor() {
		this.all = /* @__PURE__ */ new Map();
	}
	on(type, handler) {
		const handlers = this.all.get(type);
		if (handlers) handlers.push(handler);
		else this.all.set(type, [handler]);
	}
	off(type, handler) {
		const handlers = this.all.get(type);
		if (handlers) {
			if (handler) {
				const index = handlers.indexOf(handler);
				if (index !== -1) handlers.splice(index, 1);
			} else this.all.delete(type);
		}
	}
	emit(type, args) {
		const handlers = this.all.get(type);
		if (handlers) for (const handler of handlers) handler(args);
	}
};
function createLocalEvent() {
	const handlers = [];
	return {
		handlers,
		add(handler) {
			handlers.push(handler);
		},
		remove(handler) {
			return remove(handlers, handler);
		},
		raise(...args) {
			for (const handler of handlers) handler(...args);
		},
		async raiseAsync(...args) {
			await Promise.all(handlers.map((handler) => handler(...args)));
		},
		async raiseSequentiallyAsync(...args) {
			for (const handler of handlers) await handler(...args);
		}
	};
}
//#endregion
//#region src/events/eventManagerRegistry.ts
function registerEventManager(manager) {
	const global = globalThis;
	global.eventManager = manager;
}
function getEventManager() {
	const global = globalThis;
	if (!global.eventManager) throw new Error("Event manager is not registered");
	return global.eventManager;
}
//#endregion
//#region src/helpers/record.ts
/**
* Indicates whether two records are indentical or not.
* @param r1 - First record.
* @param r2 - Second record.
* @returns true when the records are identical; otherwise false.
*/
function compareRecords(r1, r2) {
	return Object.keys(r1).length === Object.keys(r2).length && Object.keys(r1).every((key) => JSON.stringify(r1[key]) === JSON.stringify(r2[key]));
}
/**
* Gets the value associated with the specified key.
*
* @param dictionary - The dictionary on which get the specified key.
* @param key - The key of the value to get.
* @param result - When this method returns, contains the value associated with the specified key, if the key is found; otherwise, undefined.
* @returns true if the dictionary contains an element with the specified key; otherwise, false.
*/
function tryGetRecordValue(dictionary, key, result) {
	result.value = dictionary[key];
	return result.value !== void 0;
}
/**
* Get the value of an object property by path.
*
* @param obj - Object.
* @param path - Path.
* @param separator - Separator.
* @returns Property value.
*/
function getPropertyByPath(obj, path, separator = ".") {
	const keys = path.split(separator);
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		if (!(key in obj)) return;
		const value = obj[key];
		if (typeof value === "object" && i < keys.length - 1) {
			if (value === null) return;
			obj = value;
			continue;
		}
		return value;
	}
}
/**
* Set the value of an object property by path.
*
* @param obj - Object.
* @param path - Path.
* @param separator - Separator.
* @param newValue - New property value.
*/
function setPropertyByPath(obj, path, newValue, separator = ".") {
	const keys = path.split(separator);
	for (let i = 0; i < keys.length - 1; i++) {
		const key = keys[i];
		if (!(key in obj)) return false;
		const value = obj[key];
		if (value === null || value === void 0 || typeof value !== "object") return false;
		obj = value;
	}
	const key = keys[keys.length - 1];
	if (key && key !== "") {
		obj[key] = newValue;
		return true;
	}
	return false;
}
//#endregion
//#region src/helpers/arraySearch.ts
/**
* Creates a search function for an array of items based on specified properties.
* Each property can optionally have a formatting function to transform the value before searching.
*
* @param array - The array of items to search within.
* @param options - An object with properties.
* @returns A function that takes a search string and returns matching items.
*/
function getArraySearchFunction(array, options) {
	const properties = Object.entries(options.properties);
	return (searchText) => {
		const normalizedSearchText = normalizeStringForSearch(searchText);
		if (!normalizedSearchText) return [];
		return array.filter((item) => properties.some(([property, { format }]) => {
			const value = getPropertyByPath(item, property);
			if (value == null) return false;
			const formattedValue = format ? format(value, item) : value;
			if (typeof formattedValue !== "string") return false;
			return normalizeStringForSearch(formattedValue).includes(normalizedSearchText);
		}));
	};
}
function normalizeStringForSearch(str) {
	return str.normalize("NFD").replaceAll(/[\u0300-\u036F]/g, "").toLowerCase();
}
//#endregion
//#region src/helpers/timeOnly.ts
/**
* Creates a TimeOnly string.
* @param hour - The hour
* @param minute - The minute
* @param second - The second
* @param millisecond - The millisecond
* @returns The TimeOnly string.
*/
function createTimeOnly(hour, minute, second, millisecond) {
	return dateTimeToTimeOnly(new Date(2e3, 1, 1, hour, minute, second, millisecond));
}
/**
* Creates a TimeOnly string from a Date.
* @param date - The date
* @returns The TimeOnly string.
*/
function dateTimeToTimeOnly(date) {
	return formatDateTime(date, "HH:mm:ss.fff");
}
var timeOnlyRegex = /^(?<hour>\d\d):(?<minute>\d\d)(:(?<second>\d\d)((\.(?<ms>\d{1,3})\d*))?)?$/;
/**
* Parses a TimeOnly string and returns parts.
* @param time - The TimeOnly string
* @returns The parts.
*/
function parseTimeOnly(time) {
	const match = timeOnlyRegex.exec(time);
	if (match?.groups) {
		const hour = Number.parseInt(match.groups.hour, 10);
		const minute = Number.parseInt(match.groups.minute, 10);
		let second = 0;
		if (match.groups.second) second = Number.parseInt(match.groups.second, 10);
		let millisecond = 0;
		if (match.groups.ms) millisecond = Number.parseInt(match.groups.ms, 10);
		return {
			hour,
			minute,
			second,
			millisecond
		};
	}
	throw new Error(`The '${time}' format is not supported.`);
}
//#endregion
//#region src/helpers/dateOnly.ts
var dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
/**
* Creates a DateOnly string.
* @param year - The year
* @param month - The month
* @param day - The day
* @returns A DateOnly string.
*/
function createDateOnly(year, month, day) {
	return dateTimeToDateOnly(new Date(year, month - 1, day));
}
/**
* Creates a DateOnly string from a Date.
* @param date - The date
* @returns The DateOnly string.
*/
function dateTimeToDateOnly(date) {
	return formatDateTime(date, "yyyy-MM-dd");
}
/**
* Adds a specified amount to a given date.
* @param value - The date to which the amount is to be added.
* @param unit - The unit of the amount to be added.
* @param amount - The amount to be added to the date.
* @returns The new date after adding the specified amount.
*/
function addToDateOnly(date, unit, amount) {
	return dateTimeToDateOnly(DateTime.fromJSDate(dateOnlyToDateTime(date)).plus({ [unit]: amount }).toJSDate());
}
/**
* Adds days to a DateOnly string.
* @param date - The date
* @param days - The day count to add
* @returns The updated DateOnly string.
*/
function addDaysToDateOnly(date, days) {
	return addToDateOnly(date, "days", days);
}
/**
* Adds months to a DateOnly string.
* @param date - The date
* @param months - The month count to add
* @returns The updated DateOnly string.
*/
function addMonthsToDateOnly(date, months) {
	return addToDateOnly(date, "months", months);
}
/**
* Adds years to a DateOnly string.
* @param date - The date
* @param years - The year count to add
* @returns The updated DateOnly string.
*/
function addYearsToDateOnly(date, years) {
	return addToDateOnly(date, "years", years);
}
/**
* Returns the start of a specified unit for a given date.
* @param value - The date for which to set the start of the unit.
* @param unit - The unit for which to set the start.
* @returns The new date after setting the start of the specified unit.
*/
function startOfDateOnly(date, unit) {
	return dateTimeToDateOnly(DateTime.fromJSDate(dateOnlyToDateTime(date)).startOf(unit).toJSDate());
}
/**
* Returns the end of a specified unit for a given date.
* @param value - The date for which to set the end of the unit.
* @param unit - The unit for which to set the end.
* @returns The new date after setting the end of the specified unit.
*/
function endOfDateOnly(date, unit) {
	return dateTimeToDateOnly(DateTime.fromJSDate(dateOnlyToDateTime(date)).endOf(unit).toJSDate());
}
/**
* Converts a DateOnly string to a Date.
* @param date - The DateOnly string
* @param time - The time of the generated Date
* @returns The Date.
*/
function dateOnlyToDateTime(date, time) {
	let dateTime = new Date(date);
	let timeParts = {
		hour: 0,
		minute: 0,
		second: 0,
		millisecond: 0
	};
	if (time) timeParts = parseTimeOnly(time);
	dateTime = DateTime.local(dateTime.getUTCFullYear(), dateTime.getUTCMonth() + 1, dateTime.getUTCDate(), timeParts.hour, timeParts.minute, timeParts.second, timeParts.millisecond).toJSDate();
	return dateTime;
}
/**
* Converts a DateOnly string to a short date string.
* @param date - The DateOnly string
* @returns The formatted text.
*/
function dateOnlyToShortDateString(date) {
	return DateTime.fromJSDate(dateOnlyToDateTime(date)).toLocaleString(DateTime.DATE_SHORT);
}
/**
* Converts a DateOnly string to a formatted string.
* @param date - The DateOnly string
* @param format - The format
* @param culture - The culture
* @returns The formatted text.
*/
function dateOnlyToString(date, format, culture) {
	const dateTime = dateOnlyToDateTime(date);
	if (format) return formatDateTime(dateTime, format, culture);
	return defaultFormatDateOnly(dateTime, culture);
}
/**
* Compares two instances of DateOnly and returns an integer that indicates whether the first instance is earlier than, the same as, or later than the second instance.
* @param dateOnly1 - The first dateOnly to compare.
* @param dateOnly2 - The second dateOnly to compare.
*/
function compareDateOnly(dateOnly1, dateOnly2) {
	const diff = dateOnlyToDateTime(dateOnly1).getTime() - dateOnlyToDateTime(dateOnly2).getTime();
	if (diff > 0) return 1;
	if (diff < 0) return -1;
	return 0;
}
/**
* Format a date to the default format according to the specified culture.
* @param date - The date to format.
* @param culture - The culture (ex: en, en-US, fr)
*/
function defaultFormatDateOnly(date, culture) {
	return (culture ? DateTime.fromJSDate(date).setLocale(culture) : DateTime.fromJSDate(date)).toLocaleString(DateTime.DATE_SHORT);
}
//#endregion
//#region src/helpers/string.ts
/**
* Specifies the culture, case and sorting rules to use for string comparison.
*/
var StringComparison = /* @__PURE__ */ function(StringComparison) {
	/**
	* Compare strings using culturally sensitive sorting rules and current culture.
	*/
	StringComparison[StringComparison["CurrentCulture"] = 0] = "CurrentCulture";
	/**
	* Compare strings using culture-sensitive sorting rules, current culture and
	* ignoring the case of the strings being compared.
	*/
	StringComparison[StringComparison["CurrentCultureIgnoreCase"] = 1] = "CurrentCultureIgnoreCase";
	/**
	* Compare strings using culture sensitive sorting rules and culture invariant.
	*/
	StringComparison[StringComparison["InvariantCulture"] = 2] = "InvariantCulture";
	/**
	* Compare strings using culture-sensitive sorting rules, culture invariant and
	* ignoring the case of the strings being compared.
	*/
	StringComparison[StringComparison["InvariantCultureIgnoreCase"] = 3] = "InvariantCultureIgnoreCase";
	/**
	* Compare strings using ordinal (binary) sorting rules.
	*/
	StringComparison[StringComparison["Ordinal"] = 4] = "Ordinal";
	/**
	* Compare strings using ordinal (binary) sorting rules and
	* ignoring the case of the strings being compared.
	*/
	StringComparison[StringComparison["OrdinalIgnoreCase"] = 5] = "OrdinalIgnoreCase";
	return StringComparison;
}({});
/**
* Specifies the invariant language code.
*/
var InvariantLanguageTag = "en-US";
/**
* Compares two specified String objects using the specified rules and
* returns an integer value that indicates their relative position in the sort order.
*
* @param strA - The first string to compare.
* @param strB - The second string to compare.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns 32-bit signed integer that indicates the lexical relationship between the two comparators.
*
* Evaluate           State
* -----------------------------------------------------------------------------
* Less than zero     strA precedes strB in the sort order.
* -----------------------------------------------------------------------------
* Zero               strA is in the same position as strB in the sort order.
* -----------------------------------------------------------------------------
* Greater than zero  strA follows strB in the sort order.
*/
function compare(strA, strB, comparisonType) {
	switch (comparisonType) {
		case 0: return strA.localeCompare(strB);
		case 1: return strA.localeCompare(strB, void 0, { sensitivity: "base" });
		case 2: return strA.localeCompare(strB, InvariantLanguageTag);
		case 3: return strA.localeCompare(strB, InvariantLanguageTag, { sensitivity: "base" });
		case 4: return compareOrdinal(strA, strB);
		case 5: return compareOrdinal(strA.toUpperCase(), strB.toUpperCase());
		default: throw new Error("'comparisonType' argument is invalid.");
	}
}
/**
* Compares two specified String objects using the specified rules and
* returns an integer that indicates their relative position in the sort order.
*
* @param strA - The first string to be used in the comparison.
* @param indexA - The starting index of the substring in strA.
* @param strB - The second string to use in the comparison.
* @param indexB - The starting index of the substring in strB.
* @param length - The maximum number of characters in the substrings to compare.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns 32-bit signed integer that indicates the lexical relationship between the two comparators.
*
* Evaluate           State
* -----------------------------------------------------------------------------
* Less than zero     strA precedes strB in the sort order.
* -----------------------------------------------------------------------------
* Zero               strA is in the same position as strB in the sort order.
* -----------------------------------------------------------------------------
* Greater than zero  strA follows strB in the sort order.
*/
function compareFromIndex(strA, indexA, strB, indexB, length, comparisonType) {
	if (indexA < 0) throw new RangeError("'indexA' argument is out of range.");
	if (indexB < 0) throw new RangeError("'indexB' argument is out of range.");
	if (length < 0) throw new RangeError("'length' argument is out of range.");
	return compare(strA.substring(indexA, indexA + length), strB.substring(indexB, indexB + length), comparisonType);
}
/**
* Compares two specified string objects by evaluating the numeric values of the corresponding characters in each string.
*
* @param strA - The first string to compare.
* @param strB - The second string to compare.
* @returns 32-bit signed integer that indicates the lexical relationship between the two comparators.
*
* Evaluate State
* -----------------------------------------------------------------------------
* Less than zero     strA precedes strB in sort order.
* -----------------------------------------------------------------------------
* Zero               strA is in the same position as strB in the sort order.
* -----------------------------------------------------------------------------
* Greater than zero  strA follows strB in the sort order.
*/
function compareOrdinal(strA, strB) {
	if (strA === strB) return 0;
	if (strA > strB) return 1;
	return -1;
}
/**
* Compares the substrings of two specified String objects by evaluating
* the numeric values of the corresponding characters in each substring.
*
* @param strA - The first string to be used in the comparison.
* @param indexA - The starting index of the substring in strA.
* @param strB - The second string to use in the comparison.
* @param indexB - The starting index of the substring in strB.
* @param length - The maximum number of characters in the substrings to compare.
* @returns 32-bit signed integer that indicates the lexical relationship between the two comparators.
*
* Evaluate           State
* -----------------------------------------------------------------------------
* Less than zero     strA precedes strB in sort order.
* -----------------------------------------------------------------------------
* Zero               strA is in the same position as strB in the sort order.
* -----------------------------------------------------------------------------
* Greater than zero  strA follows strB in the sort order.
*/
function compareOrdinalFromIndex(strA, indexA, strB, indexB, length) {
	if (indexA < 0) throw new RangeError("'indexA' argument is out of range.");
	if (indexB < 0) throw new RangeError("'indexB' argument is out of range.");
	if (length < 0) throw new RangeError("'length' argument is out of range.");
	return compareOrdinal(strA.substring(indexA, indexA + length), strB.substring(indexB, indexB + length));
}
/**
* Concatenates the elements of a specified array.
*
* @param values - An array of object instances.
* @returns A string representing the concatenated elements of values.
*/
function concat(...values) {
	const items = [];
	for (const v of values) if (Array.isArray(v)) items.push(...v);
	else items.push(v);
	return items.join("");
}
/**
* Returns a value indicating whether a specified string appears in the search string, using the specified comparison rules.
*
* @param str - The search string.
* @param substr - The string to search for.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns true if the value parameter appears in the string; otherwise, false.
*/
function contains(str, substr, comparisonType) {
	return indexOf(str, substr, comparisonType) !== -1;
}
/**
* Returns a value indicating whether the end of the string matches the specified string
* when compared using the specified comparison option.
*
* @param str - The search string.
* @param substr - The string to search for.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns true if the value parameter matches the end of the string; otherwise, false.
*/
function endsWith(str, substr, comparisonType) {
	switch (comparisonType) {
		case 0:
		case 2:
		case 4: return str.endsWith(substr);
		case 1: return str.toLocaleUpperCase().endsWith(substr.toLocaleUpperCase());
		case 3: return str.toLocaleUpperCase(InvariantLanguageTag).endsWith(substr.toLocaleUpperCase(InvariantLanguageTag));
		case 5: return str.toUpperCase().endsWith(substr.toUpperCase());
		default: throw new Error("'comparisonType' argument is invalid.");
	}
}
/**
* Returns a value indicating whether the two specified strings have the same value when compared using the specified comparison option.
*
* @param strA - The first string to compare.
* @param strB - The second string to compare.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns true if the strA parameter is equal to the strB parameter; otherwise, false.
*/
function equals(strA, strB, comparisonType) {
	if (strA == null && strB == null) return true;
	if (strA == null || strB == null) return false;
	return equalsInternal(strA, strB, comparisonType);
}
function equalsInternal(strA, strB, comparisonType) {
	switch (comparisonType) {
		case 0: return strA.localeCompare(strB) === 0;
		case 1: return strA.localeCompare(strB, void 0, { sensitivity: "base" }) === 0;
		case 2: return strA.localeCompare(strB, InvariantLanguageTag) === 0;
		case 3: return strA.localeCompare(strB, InvariantLanguageTag, { sensitivity: "base" }) === 0;
		case 4: return compareOrdinal(strA, strB) === 0;
		case 5: return compareOrdinal(strA.toUpperCase(), strB.toUpperCase()) === 0;
		default: throw new Error("'comparisonType' argument is invalid.");
	}
}
/**
* Returns the base zero index of the first occurrence of the specified string in the search string.
* A parameter specifies the type of search to use.
*
* @param str - The search string.
* @param substr - The string to search.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns The index position of the substr parameter if this string is found, or -1 if it is not.
*/
function indexOf(str, substr, comparisonType) {
	return indexOfFromIndex(str, substr, 0, comparisonType);
}
/**
* Returns the base zero index of the first occurrence of the specified string in the search string.
* Parameters specify the starting position of the search in the string and the type of search to use.
*
* @param str - The search string.
* @param substr - The string to search.
* @param startIndex - The starting position for the search.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns The index position of the substr parameter if this string is found, or -1 if it is not.
*/
function indexOfFromIndex(str, substr, startIndex, comparisonType) {
	if (startIndex < 0) throw new RangeError("'startIndex' argument is out of range.");
	if (isNullOrEmpty(substr)) return startIndex;
	switch (comparisonType) {
		case 0:
		case 2:
		case 4: return str.indexOf(substr, startIndex);
		case 1: return str.toLocaleUpperCase().indexOf(substr.toLocaleUpperCase(), startIndex);
		case 3: return str.toLocaleUpperCase(InvariantLanguageTag).indexOf(substr.toLocaleUpperCase(InvariantLanguageTag), startIndex);
		case 5: return str.toUpperCase().indexOf(substr.toUpperCase(), startIndex);
		default: throw new Error("'comparisonType' argument is invalid.");
	}
}
/**
* Returns the base zero index of the first occurrence of the specified string in the search string.
* The parameters specify the starting position of the search in the string,
* the number of characters in the current string to search and the type of search to use.
*
* @param str - The search string.
* @param substr - The string to search.
* @param startIndex - The starting position for the search.
* @param length - The maximum number of characters to examine.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns The index position of the substr parameter if this string is found, or -1 if it is not.
*/
function indexOfFromIndexAndLength(str, substr, startIndex, length, comparisonType) {
	if (startIndex < 0) throw new RangeError("'startIndex' argument is out of range.");
	if (length < 0) throw new RangeError("'length' argument is out of range.");
	const index = indexOf(str.substring(startIndex, startIndex + length), substr, comparisonType);
	return index !== -1 ? startIndex + index : index;
}
/**
* Indicates whether the specified string is null or an empty string.
*
* @param s - The string to test.
* @returns true if the value parameter is null or an empty string, otherwise false.
*/
function isNullOrEmpty(s) {
	return !s || s.length === 0;
}
/**
* Indicates whether a specified string is null, empty, or consists only of white-space characters.
*
* @param s - The string to test.
* @returns true if the value parameter is null or Empty, or if value consists exclusively of white-space characters.
*/
function isNullOrWhiteSpace(s) {
	return !s || isWhiteSpace(s);
}
/**
* Indicates whether a specified string is empty or consists only of white-space characters.
* @param s - The string to test.
* @returns true if the value parameter is Empty or if value consists exclusively of white-space characters.
*/
function isWhiteSpace(s) {
	return /^\s*$/.test(s);
}
/**
* Returns the base zero index of the first occurrence of the specified string in the search string.
* A parameter specifies the type of search to use.
*
* @param str - The search string.
* @param substr - The string to search.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns The index position of the substr parameter if this string is found, or -1 if it is not.
*/
function lastIndexOf(str, substr, comparisonType) {
	return lastIndexOfFromIndex(str, substr, str.length - 1, comparisonType);
}
/**
* Returns the base zero index of the first occurrence of the specified string in the search string.
* Parameters specify the starting position of the search in the string and the type of search to use.
*
* @param str - The search string.
* @param substr - The string to search.
* @param startIndex - The starting position for the search.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns The index position of the substr parameter if this string is found, or -1 if it is not.
*/
function lastIndexOfFromIndex(str, substr, startIndex, comparisonType) {
	if (startIndex < 0) throw new RangeError("'startIndex' argument is out of range.");
	switch (comparisonType) {
		case 0:
		case 2:
		case 4: return str.lastIndexOf(substr, startIndex);
		case 1: return str.toLocaleUpperCase().lastIndexOf(substr.toLocaleUpperCase(), startIndex);
		case 3: return str.toLocaleUpperCase(InvariantLanguageTag).lastIndexOf(substr.toLocaleUpperCase(InvariantLanguageTag), startIndex);
		case 5: return str.toUpperCase().lastIndexOf(substr.toUpperCase(), startIndex);
		default: throw new Error("'comparisonType' argument is invalid.");
	}
}
/**
* Returns the base zero index of the first occurrence of the specified string in the search string.
* The parameters specify the starting position of the search in the string,
* the number of characters in the current string to search and the type of search to use.
*
* @param str - The search string.
* @param substr - The string to search.
* @param startIndex - The starting position for the search.
* @param length - The maximum number of characters to examine.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns The index position of the substr parameter if this string is found, or -1 if it is not.
*/
function lastIndexOfFromIndexAndLength(str, substr, startIndex, length, comparisonType) {
	if (startIndex < 0) throw new RangeError("'startIndex' argument is out of range.");
	if (length < 0) throw new RangeError("'length' argument is out of range.");
	const endIndex = startIndex - length + 1;
	if (endIndex < 0) throw new RangeError("'length' argument is out of range.");
	const index = lastIndexOf(str.substring(endIndex, endIndex + length), substr, comparisonType);
	return index !== -1 ? endIndex + index : index;
}
/**
* Returns a new string in which all occurrences of a specified string in the current instance are replaced
* with another specified string, using the provided comparison type.
*
* @param str - The search string.
* @param oldValue - The string to be replaced.
* @param newValue - The string to replace all occurrences of oldValue.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns A string that is equivalent to the current string except that all instances of oldValue are replaced with newValue.
*          If oldValue is not found in the current instance, the method returns the current instance unchanged.
*/
function replace(str, oldValue, newValue, comparisonType) {
	if (isNullOrEmpty(oldValue)) throw new Error("'oldValue' argument is null or empty.");
	switch (comparisonType) {
		case 0:
		case 2:
		case 4: return str.replace(oldValue, newValue);
		case 1:
		case 3:
		case 5: {
			let s = str;
			const oldLength = oldValue.length;
			const newLength = newValue.length;
			let index = 0;
			while ((index = indexOfFromIndex(s, oldValue, index, comparisonType)) !== -1) {
				s = [
					s.slice(0, index),
					newValue,
					s.slice(index + oldLength)
				].join("");
				index += newLength;
			}
			return s;
		}
		default: throw new Error("'comparisonType' argument is invalid.");
	}
}
/**
* Returns a new string with all search string occurences is replaced by a replacement string.
* @param value - The search string.
* @param search - The string to be replaced.
* @param replacement - The string to replace all occurrences of search.
*/
function replaceAll(value, search, replacement) {
	return value.replaceAll(search, replacement);
}
/**
* Returns a value indicating whether the specified string and value have the same value when compared.
*
* @param s - The first one to compare.
* @param value - The value to compare.
* @returns true if parameter s is equal to parameter value; otherwise, false.
*/
function sameValue(s, value) {
	if (value == null || typeof value !== "string") return false;
	return value.toString() === s;
}
/**
* Returns a value indicating whether the beginning of this string matches the specified string
* when compared using the specified comparison option.
*
* @param str - The search string.
* @param substr - The string to search for.
* @param comparisonType - One of the enumeration values that specifies the rules to use in the comparison.
* @returns true if the value parameter matches the end of the string; otherwise, false.
*/
function startsWith(str, substr, comparisonType) {
	switch (comparisonType) {
		case 0:
		case 2:
		case 4: return str.startsWith(substr);
		case 1: return str.toLocaleUpperCase().startsWith(substr.toLocaleUpperCase());
		case 3: return str.toLocaleUpperCase(InvariantLanguageTag).startsWith(substr.toLocaleUpperCase(InvariantLanguageTag));
		case 5: return str.toUpperCase().startsWith(substr.toUpperCase());
		default: throw new Error("'comparisonType' argument is invalid.");
	}
}
/**
* Removes spaces at the end of all lines and deletes empty lines at the end of text.
*
* @param text - A multi-line text.
* @returns A string whitout spaces at the end of lines and with no trailing empty lines.
*/
function trimEndLines(text) {
	return text.replaceAll(/[ \t]+(\r*\n)/gi, "$1").trimEnd();
}
/**
* Convert text to camelCase.
*
* @param text - The text to convert to camelCase.
* @param removeSpace - A boolean value indicating whether all spaces will be removed or not. In this case, the first letters after spaces are capitalized
* @returns A camelCase string.
*/
function toCamelCase(text, removeSpace) {
	if (text.trim().length === 0 || !/[A-Z]/.test(text[0]) && !removeSpace) return text;
	let result = toCamelCaseInternal(text).join("");
	if (removeSpace) {
		result = result.split(" ").map((i) => i.charAt(0).toUpperCase() + i.slice(1)).join("");
		result = result.charAt(0).toLowerCase() + result.slice(1);
	}
	return result;
}
function toCamelCaseInternal(text) {
	const charArray = [...text];
	for (let i = 0; i < charArray.length; i++) {
		if (i === 1 && !/[A-Z]/.test(charArray[1])) break;
		const hasNext = i + 1 < charArray.length;
		if (i > 0 && hasNext && !/[A-Z]/.test(charArray[i + 1])) {
			if (/\s/.test(charArray[i + 1])) charArray[i] = charArray[i].toLowerCase();
			break;
		}
		charArray[i] = charArray[i].toLowerCase();
	}
	return charArray;
}
/**
* Convert text to PascalCase.
*
* @param text - The text to convert to PascalCase.
* @returns A PascalCase string.
*/
function toPascalCase(text) {
	if (text.trim().length === 0) return text;
	const alphaNumericMatches = text.match(/[a-zA-Z0-9]+/g);
	if (alphaNumericMatches === null) return text;
	return alphaNumericMatches.map((w) => `${w.charAt(0).toUpperCase()}${w.slice(1)}`).join("");
}
/**
* Transform the start of a text to lower case when the text start with one capital letter.
* @param text Text to convert.
* @returns The text with the first letter in lower case.
*/
function toLowerCaseStart(text) {
	if ((/* @__PURE__ */ new RegExp(/^[A-ZÀ-Ý][a-zà-ÿ ]/)).exec(text) !== null) return `${text.charAt(0).toLocaleLowerCase()}${text.slice(1)}`;
	return text;
}
/**
* Checks if the text has no leading and trailing white space.
* @param text
* @returns
*/
function hasNoLeadingAndTrailingWhiteSpace(text) {
	return text.length === 0 || !text.startsWith(" ") && !text.endsWith(" ");
}
/**
* Checks if the text has no leading white space.
* @param text
* @returns
*/
function hasNoLeadingWhiteSpace(text) {
	return text.length === 0 || !text.startsWith(" ");
}
/**
* Checks if the text has no trailing white space.
* @param text
* @returns
*/
function hasNoTrailingWhiteSpace(text) {
	return text.length === 0 || !text.endsWith(" ");
}
/**
* Checks if the text has no white space.
* @param text
* @returns
*/
function hasNoWhiteSpace(text) {
	return text.length === 0 || !text.includes(" ");
}
/**
* Trims the specified characters from the beginning, end, or both of a string.
* @param value The string to trim.
* @param chars Array of characters to remove.
* @param mode 'both' (default), 'start', or 'end'.
*/
function trimChars(value, chars, mode = "both") {
	if (!value || chars.length === 0) return value;
	const pattern = `[${chars.map((c) => `\\${c}`).join("")}]`;
	if (mode === "start") return value.replaceAll(new RegExp(`^${pattern}+`, "g"), "");
	if (mode === "end") return value.replaceAll(new RegExp(`${pattern}+$`, "g"), "");
	return value.replaceAll(new RegExp(`^${pattern}+|${pattern}+$`, "g"), "");
}
//#endregion
//#region src/helpers/dictionary.ts
/**
* Gets the value associated with the specified key.
*
* @param dictionary - The dictionary on which get the specified key.
* @param key - The key of the value to get.
* @param result - When this method returns, contains the value associated with the specified key, if the key is found; otherwise, undefined.
* @returns true if the dictionary contains an element with the specified key; otherwise, false.
*/
function tryGetValue(dictionary, key, result) {
	result.value = dictionary.get(key);
	return result.value !== void 0;
}
/**
* Converts a Map to a Record.
* @param dictionary - Map.
* @returns Record.
*/
function toRecord(dictionary) {
	const record = {};
	for (const [key, value] of dictionary) record[key] = value;
	return record;
}
/**
* Converts a Record to a Map.
* @param record - Record.
* @returns Map.
*/
function fromRecord(record) {
	return new Map(Object.entries(record));
}
/**
* Compares two dictionaries and returns a boolean indicating whether the two dictionaries are equivalent.
* @param dic1 - First dictionary.
* @param dic2 - Second dictionary.
* @returns A value indicating whether the two dictionnaires are equivalent.
*/
function compareDictionaries(dic1, dic2) {
	if (dic1.size !== dic2.size) return false;
	for (const [key, value1] of dic1) {
		const value2 = dic2.get(key);
		if (value2 === void 0 && !dic2.has(key) || JSON.stringify(value1) !== JSON.stringify(value2)) return false;
	}
	return true;
}
/**
* Converts key to camelCase.
* @param map - the dictionary to convert.
* @returns A new dictionary.
*/
function toCamelCaseKey(map) {
	const convertedMap = /* @__PURE__ */ new Map();
	map.forEach((value, key) => convertedMap.set(toCamelCase(key), value));
	return convertedMap;
}
//#endregion
//#region src/helpers/enum.ts
/**
* Converts a value from an enum to the equivalent value in another enum.
* Values a are converted by matching the enum keys in each enumeration.
* @param originValue - Origin value.
* @param originEnum - Origin enumeration.
* @param resultEnum - Result enumeration.
* @returns Converted value or undefined if corresponding value cannot be found in the result enum or cannot converted to the expected result type.
*/
function convertToOtherEnum(originValue, originEnum, resultEnum) {
	const originValueIndex = Object.values(originEnum).indexOf(originValue);
	if (originValueIndex === -1) return;
	const originKey = Object.keys(originEnum)[originValueIndex];
	const resultKeyIndex = Object.keys(resultEnum).indexOf(originKey);
	if (resultKeyIndex === -1) return;
	return Object.values(resultEnum)[resultKeyIndex];
}
/**
* Gets an enumeration value from a key.
* @param key - Key.
* @param enumeration - Enumeration.
* @returns Enumeration value.
*/
function getEnumValue(key, enumeration) {
	const keyIndex = Object.keys(enumeration).indexOf(key);
	if (keyIndex === -1) return;
	return Object.values(enumeration)[keyIndex];
}
//#endregion
//#region src/helpers/json.ts
function defaultJsonReviver(_key, value) {
	if (typeof value === "string") {
		const matches = dateTimeWithOptionalTimezoneRegex.exec(value);
		if (matches) {
			if (!matches[2] && !matches[3]) return new InvariantDateTime(value);
			return new Date(value);
		}
	}
	return value;
}
//#endregion
//#region src/vsCodeExt/vsCode.ts
var _vscode;
var _viewType;
function getVsCode() {
	_vscode ??= acquireVsCodeApi();
	return _vscode;
}
/** Posts a message to VSCode extension host */
function postMessageToVsCode(args) {
	getVsCode().postMessage({
		...args,
		viewType: _viewType
	});
}
function setViewType(value) {
	_viewType = value;
}
function updateViewTypeWithId(id) {
	const viewTypeParts = _viewType?.split(":");
	if (viewTypeParts && viewTypeParts.length > 1) {
		viewTypeParts[viewTypeParts.length - 1] = id;
		_viewType = viewTypeParts.join(":");
	}
}
/** Reset the module state  */
function resetVsCodeState() {
	_vscode = void 0;
	_viewType = void 0;
}
//#endregion
//#region src/helpers/location.ts
var baseUrl;
/**
* Gets the base url of the application.
*/
function getBaseUrl() {
	if (baseUrl === void 0) {
		baseUrl = document.querySelector("base")?.getAttribute("href") ?? void 0;
		if (baseUrl === void 0) {
			if (location.protocol === "blob:") baseUrl = location.pathname;
			else baseUrl = `${location.protocol}//${location.host}`;
		}
		if (baseUrl.endsWith("/")) baseUrl = baseUrl.substring(0, baseUrl.length - 1);
		else if (baseUrl.endsWith("/index.html")) baseUrl = baseUrl.substring(0, baseUrl.length - 11);
	}
	return baseUrl;
}
function stripLastUrlSegment(url) {
	const lastSlashIndex = url.lastIndexOf("/");
	if (lastSlashIndex === -1) return url;
	return url.substring(0, lastSlashIndex);
}
/** Sets the base url of the application.
* @param url The base url to set.
*/
function setBaseUrl(url) {
	baseUrl = url;
}
/** Resets the cached value of the base Url */
function resetBaseUrl() {
	baseUrl = void 0;
}
/**
* Gets the cluster root base url, stripping the tenant segment when the
* \<base\> tag carries a `data-tenant` attribute (multitenant prefixed deployment).
*/
function getClusterBaseUrl() {
	const tenant = document.querySelector("base")?.dataset.tenant;
	const url = getBaseUrl();
	if (tenant && url.endsWith(`/${tenant}`)) return stripLastUrlSegment(url);
	return url;
}
/** Replaces the location path to the specified path  */
function replaceLocationPath(path) {
	window.location.replace(`${getClusterBaseUrl()}/${path}`);
}
/** Converts relative path to absolute url */
function relativeToAbsoluteUrl(relativePath) {
	return `${getBaseUrl()}/${relativePath}`;
}
/** Opens the specified path in a new window. */
function openPath(path, target, features) {
	if (__NEOS_STUDIO_VSCODE__) {
		postMessageToVsCode({
			command: "openUrlPath",
			url: path
		});
		return null;
	}
	return window.open(`${getClusterBaseUrl()}/${path}`, target, features);
}
/** Gets the query parameters */
function getQueryParameters() {
	const parameters = /* @__PURE__ */ new Map();
	let search = window.location.search;
	if (window.location.protocol === "blob:") search = window.location.hash.substring(window.location.hash.indexOf("?"));
	new URLSearchParams(search).forEach((v, k) => parameters.set(k, v));
	return parameters;
}
//#endregion
//#region src/helpers/number.ts
/**
* Describe rounding methods.
*/
var MidpointRounding = /* @__PURE__ */ function(MidpointRounding) {
	MidpointRounding[MidpointRounding["ToEven"] = 0] = "ToEven";
	MidpointRounding[MidpointRounding["AwayFromZero"] = 1] = "AwayFromZero";
	return MidpointRounding;
}({});
/**
* Rounds a decimal value to a specified number of fractional digits, and uses the specified rounding convention for midpoint values.
*
* @param value - A decimal number to be rounded.
* @param decimals - The number of decimal places in the return value.
* @param midPointRounding - Specification for how to round `value` if it is midway between two other numbers.
* @returns Rounded value.
*/
function round(value, decimals, midPointRounding = 0) {
	if (midPointRounding === 1) return roundAwayFromZero(value, decimals);
	return roundToEven(value, decimals);
}
/**
* Rounds a decimal value to a specified number of fractional digits.
*
* @param value - A decimal number to be rounded.
* @param decimals - The number of decimal places in the return value.
* @returns Rounded value.
*/
function roundToEven(value, decimals) {
	const pow = 10 ** decimals;
	const n = +(decimals ? value * pow : value).toFixed(8);
	const i = Math.floor(n);
	const f = n - i;
	let r;
	if (f > .49999999 && f < .50000001) r = i % 2 == 0 ? i : i + 1;
	else r = Math.round(n);
	return decimals ? r / pow : r;
}
/**
* Rounds a decimal value to a specified number of fractional digits.
*
* @param value - A decimal number to be rounded.
* @param decimals - The number of decimal places in the return value.
* @returns Rounded value.
*/
function roundAwayFromZero(value, decimals) {
	const pow = Decimal.pow(10, decimals);
	const decimalValue = new Decimal(value);
	if (decimalValue.greaterThanOrEqualTo(0)) return Decimal.round(decimalValue.mul(pow)).div(pow).toNumber();
	return -Decimal.round(Decimal.abs(decimalValue).mul(pow)).div(pow).toNumber();
}
/**
* Converts the string representation of a number to its integer equivalent.
* @param value - A string containing a integer to convert.
* @param result - A number equivalent to the string value or NaN if value is not a number.
* @returns A value indicating whether the string has been parsed to a number with success.
*/
function tryParseInteger(value, result) {
	if (/^\d+$/.test(value)) {
		result.value = Number.parseInt(value, 10);
		return true;
	}
	result.value = NaN;
	return false;
}
/**
* Converts a number to a string according to a specified format.
* @param val - number to format
* @param culture - culture
* @param format - format
*/
function formatNum(val, culture, format) {
	if (format) return getDotNetFormatedNum(val, format, culture);
	return new Intl.NumberFormat(culture, { useGrouping: false }).format(val);
}
/**
* Return formated number with Intl.NumberFormat.format
* (minus sign is removed if isNegativeFormat is true)
*
* @param val - Number to format.
* @param culture - Culture to use.
* @param numberFormatOptions - Intl.NumberFormatOptions instance.
* @param isNegativeFormat - If true, minus sign is removed.
*/
function getIntlNumberFormatResult(val, culture, numberFormatOptions, isNegativeFormat = false) {
	const result = new Intl.NumberFormat(culture, numberFormatOptions).format(val);
	if (isNegativeFormat) return result.slice(1);
	return result;
}
/**
* Parse the format and return the associated formated number using .Net format syntax.
* @param val - Number to format.
* @param format - Format to use.
* @param culture - Culture to use.
*/
function getDotNetFormatedNum(val, format, culture) {
	const numberFormatOptions = {};
	const dMatch = /^D(?<digits>\d*)$/i.exec(format);
	if (dMatch?.groups) {
		const digits = dMatch.groups.digits;
		return getDotNetDecimalFormatedNum(numberFormatOptions, digits, val, culture);
	}
	const eMatch = /^(?<symbol>[eE])(?<digits>\d*)$/.exec(format);
	if (eMatch?.groups) {
		const { symbol, digits } = eMatch.groups;
		return getDotNetExponentialFormatedNum(digits, val, numberFormatOptions, culture, symbol);
	}
	const fMatch = /^F(?<digits>\d*)$/i.exec(format);
	if (fMatch?.groups) {
		const digits = fMatch.groups.digits;
		return getDotNetFixedPointFormatedNum(numberFormatOptions, digits, val, culture);
	}
	const nMatch = /^N(?<digits>\d*)$/i.exec(format);
	if (nMatch?.groups) {
		const digits = nMatch.groups.digits;
		return getDotNetNumericFormatedNum(digits, numberFormatOptions, val, culture);
	}
	const pMatch = /^P(?<digits>\d*)$/i.exec(format);
	if (pMatch?.groups) {
		const digits = pMatch.groups.digits;
		return getDotNetPercentFormatedNum(digits, numberFormatOptions, val, culture);
	}
	if (format.toLowerCase() === "r") return getRoundTripFormatedNum(val, numberFormatOptions, culture);
	const xMatch = /^(?<symbol>[xX])(?<digits>\d*)$/.exec(format);
	if (xMatch?.groups) {
		const { symbol, digits } = xMatch.groups;
		return getHexadecimalFormatedNum(val, symbol, digits);
	}
	return getCustomFormatNum(format, val, numberFormatOptions, culture);
}
/**
* Returns hexadecimal formated number whith X or x: A hexadecimal string.
* @param val - Number to format.
* @param symbol - Case sensitive hexadecimal symbol (X or x).
* @param digits - Digit part.
*/
function getHexadecimalFormatedNum(val, symbol, digits) {
	let result = (val >>> 0).toString(16);
	if (symbol === "X") result = result.toUpperCase();
	const digitsToAdd = (Number.parseInt(digits || "", 10) || 0) - result.length;
	if (digitsToAdd > 0) result = `${"0".repeat(digitsToAdd)}${result}`;
	return result;
}
/**
* Returns round-trip formated number whith R or r: A string that can round-trip to an identical number.
* @param numberFormatOptions - Int.NumberFormatOptions instance.
* @param val - Number to format.
* @param culture - Culture to use.
*/
function getRoundTripFormatedNum(val, numberFormatOptions, culture) {
	const digits = val.toString().split(".")[1]?.length || 0;
	numberFormatOptions.minimumFractionDigits = digits;
	numberFormatOptions.maximumFractionDigits = digits;
	numberFormatOptions.useGrouping = false;
	return getIntlNumberFormatResult(val, culture, numberFormatOptions);
}
/**
* Returns percent formated number whith P or p: Number multiplied by 100 and displayed with a percent symbol.
* @param numberFormatOptions - Int.NumberFormatOptions instance.
* @param digits - Digit part.
* @param val - Number to format.
* @param culture - Culture to use.
*/
function getDotNetPercentFormatedNum(digits, numberFormatOptions, val, culture) {
	const digitsNb = Number.parseInt(digits || "", 10) || 2;
	numberFormatOptions.style = "percent";
	numberFormatOptions.minimumFractionDigits = digitsNb;
	numberFormatOptions.maximumFractionDigits = digitsNb;
	return getIntlNumberFormatResult(val, culture, numberFormatOptions);
}
/**
* Returns numeric formated number whith N or n: Integral and decimal digits, group separators, and a decimal separator with optional negative sign.
* @param numberFormatOptions - Int.NumberFormatOptions instance.
* @param digits - Digit part.
* @param val - Number to format.
* @param culture - Culture to use.
*/
function getDotNetNumericFormatedNum(digits, numberFormatOptions, val, culture) {
	let digitsNb = Number.parseInt(digits || "", 10);
	if (Number.isNaN(digitsNb)) digitsNb = 2;
	numberFormatOptions.minimumFractionDigits = digitsNb;
	numberFormatOptions.maximumFractionDigits = digitsNb;
	return getIntlNumberFormatResult(val, culture, numberFormatOptions);
}
/**
* Returns fixed point formated number whith F or f: Integral and decimal digits with optional negative sign.
* @param numberFormatOptions - Int.NumberFormatOptions instance.
* @param digits - Digit part.
* @param val - Number to format.
* @param culture - Culture to use.
*/
function getDotNetFixedPointFormatedNum(numberFormatOptions, digits, val, culture) {
	numberFormatOptions.useGrouping = false;
	const digitsNb = Number.parseInt(digits || "", 10) || 2;
	numberFormatOptions.minimumFractionDigits = digitsNb;
	numberFormatOptions.maximumFractionDigits = digitsNb;
	return getIntlNumberFormatResult(val, culture, numberFormatOptions);
}
/**
* Returns exponential formated number whith E or e: Exponential notation.
* @param numberFormatOptions - Int.NumberFormatOptions instance.
* @param digits - Digit part.
* @param val - Number to format.
* @param culture - Culture to use.
* @param symbol - Case sensitive exponential symbol (E or e).
*/
function getDotNetExponentialFormatedNum(digits, val, numberFormatOptions, culture, symbol) {
	const digitsNb = Number.parseInt(digits || "", 10) || 6;
	const baseExpNotation = val.toExponential(digitsNb).split("e");
	const numberPart = Number.parseFloat(baseExpNotation[0]);
	numberFormatOptions.minimumFractionDigits = digitsNb;
	numberFormatOptions.maximumFractionDigits = digitsNb;
	const formatedNumberPart = new Intl.NumberFormat(culture, numberFormatOptions).format(numberPart);
	const expPart = baseExpNotation[1];
	return `${formatedNumberPart}${symbol}${expPart.startsWith("+") ? "+" : ""}${new Intl.NumberFormat(culture, {
		useGrouping: false,
		minimumIntegerDigits: 3
	}).format(Number.parseInt(expPart, 10))}`;
}
/**
* Returns decimal formated number whith D or d: Integer digits with optional negative sign.
* @param numberFormatOptions - Int.NumberFormatOptions instance.
* @param digits - Digit part.
* @param val - Number to format.
* @param culture - Culture to use.
*/
function getDotNetDecimalFormatedNum(numberFormatOptions, digits, val, culture) {
	numberFormatOptions.useGrouping = false;
	numberFormatOptions.minimumIntegerDigits = Number.parseInt(digits || "", 10) || void 0;
	return getIntlNumberFormatResult(val, culture, numberFormatOptions);
}
/**
* Parses custom .Net format and returns formated number.
*
* * ; : Defines sections with separate format strings for positive, negative, and zero numbers.
* * 0: replaces the zero with the corresponding digit if one is present; otherwise, zero appears in the result string.
* * #: Replaces the "#" symbol with the corresponding digit if one is present; otherwise, no digit appears in the result string.
* * .: Determines the location of the decimal separator in the result string.
* * ,: Serves as both a group separator and a number scaling specifier.
*    - As a group separator, it inserts a localized group separator character between each group.
*    - As a number scaling specifier, it divides a number by 1000 for each comma specified.
* * %: Multiplies a number by 100 and inserts a localized percentage symbol in the result string.
* * ‰: Multiplies a number by 1000 and inserts a localized per mille symbol in the result string.
* * backslash: Causes the next character to be interpreted as a literal rather than as a custom format specifier.
* * 'string': Indicates that the enclosed characters should be copied to the result string unchanged.
* * All other characters: The character is copied to the result string unchanged.
* @param format - Custom format.
* @param val - Value to format.
* @param numberFormatOptions - Intl.NumberFormatOptions instance.
* @param culture - Culture to use.
*/
function getCustomFormatNum(format, val, numberFormatOptions, culture) {
	const regex = /^,*(?<pre>#|(?:,*#))*(?<int>,*0(?:0|#|(?:,*[0#]))*)?(?<scaling>,+)?(?:\.(?<trailing>[#0,]+))?$/;
	const { format: conditionalFormat, isNegativeFormat } = getConditionalFormat(format, val);
	const { formatMask, numericFormat, formatReplacements } = createFormatMask(conditionalFormat);
	const customMatch = numericFormat && regex.exec(numericFormat);
	if (customMatch && customMatch.groups) {
		numberFormatOptions.useGrouping = `${customMatch.groups.int}${customMatch.groups.pre}`.includes(",");
		numberFormatOptions.minimumIntegerDigits = customMatch.groups.int?.replaceAll(",", "").length;
		numberFormatOptions.maximumFractionDigits = 0;
		if (customMatch.groups.scaling) val *= 10 ** (-3 * customMatch.groups.scaling.length);
		const trailing = customMatch.groups.trailing?.replaceAll(",", "");
		if (trailing) {
			numberFormatOptions.maximumFractionDigits = trailing.length;
			numberFormatOptions.minimumFractionDigits = trailing.lastIndexOf("0") + 1;
		}
		const percentMultiplier = 10 ** ((format.match(/%/g) || []).length * 2);
		const perthousandMultiplier = 10 ** ((format.match(/‰/g) || []).length * 3);
		val *= percentMultiplier * perthousandMultiplier;
	}
	return applyFormatMask(getIntlNumberFormatResult(val, culture, numberFormatOptions, isNegativeFormat), {
		formatMask,
		formatReplacements
	});
}
/**
* (Internal) Create a format mask for litteral chars processing
*
* @param format - format
*/
function createFormatMask(format) {
	let formatMask = "";
	let numericFormat = "";
	let escaping = false;
	let quoteStart = -1;
	const formatReplacements = [];
	format.split("").forEach((c, index) => {
		if (c === "\\") {
			escaping = true;
			return;
		}
		if (c === "'") {
			if (quoteStart >= 0) {
				const replacement = format.slice(quoteStart + 1, index);
				formatReplacements.push(replacement);
				formatMask = `${formatMask}¤`;
				quoteStart = -1;
			} else quoteStart = index;
			return;
		}
		if (quoteStart >= 0) return;
		if (!escaping && /[0#,.]/.exec(c)) {
			if (c !== ",") formatMask = `${formatMask}${c}`;
			numericFormat = `${numericFormat}${c}`;
		} else {
			formatReplacements.push(c);
			formatMask = `${formatMask}¤`;
		}
		escaping = false;
	});
	return {
		formatMask,
		numericFormat,
		formatReplacements
	};
}
/**
* (Internal) Apply a specified mask to a preformated number.
* @param preformatedNumber - Preformated number on which to apply mask.
* @param mask - Mask to apply.
*/
function applyFormatMask(preformatedNumber, mask) {
	if (!mask.formatMask.includes("¤")) return preformatedNumber;
	let result = "";
	const remainingReplacements = [...mask.formatReplacements];
	const remainingDigits = [...preformatedNumber];
	const formatMaskLength = mask.formatMask.length;
	for (let i = formatMaskLength - 1; i >= 0; i--) {
		const currentChar = mask.formatMask[i];
		if (currentChar === "¤") result = `${remainingReplacements.pop()}${result}`;
		else {
			let currentNumberPart = remainingDigits.pop() ?? "";
			if (currentChar !== "." && /[^\d]/.exec(currentNumberPart)) {
				result = `${currentNumberPart}${result}`;
				currentNumberPart = remainingDigits.pop() ?? "";
			}
			result = `${currentNumberPart}${result}`;
			if (remainingDigits.length > 0 && !/[#0]/.test(mask.formatMask.substring(0, i))) result = `${remainingDigits.join("")}${result}`;
		}
	}
	return result;
}
/**
* (Internal) Extract the right format wheter the value is positive, negative or zero (separated by non escaped ;)
* @param format - Format
* @param val - Value
*/
function getConditionalFormat(format, val) {
	const separatedFormats = [
		"",
		"",
		""
	];
	let currentFormatIndex = 0;
	let escaping = false;
	let quoting = false;
	format.split("").forEach((c) => {
		if (!escaping && !quoting && c === ";") {
			currentFormatIndex++;
			return;
		}
		separatedFormats[currentFormatIndex] = `${separatedFormats[currentFormatIndex]}${c}`;
		if (!escaping && c === "\\") {
			escaping = true;
			return;
		}
		if (!escaping && c === "'") quoting = !quoting;
		escaping = false;
	});
	const isNegativeFormat = val < 0 && separatedFormats[1].length > 0;
	const baseFormat = separatedFormats[0];
	if (val > 0) return {
		format: baseFormat,
		isNegativeFormat
	};
	if (val < 0) return {
		format: separatedFormats[1] || baseFormat,
		isNegativeFormat
	};
	return {
		format: separatedFormats[2] || baseFormat,
		isNegativeFormat
	};
}
//#endregion
//#region src/helpers/object.ts
var emptyObject = Object.create(null);
function toODataValue(value) {
	if (typeof value === "string") {
		if (isGuid(value) || dateOnlyRegex.test(value)) return value;
		return `'${value.replaceAll("'", "''")}'`;
	}
	if (typeof value === "number" || typeof value === "boolean") return value.toString();
	if (value instanceof InvariantDateTime) return toLocalIso(value);
	if (value instanceof Date) return value.toISOString();
	if (value === null) return "null";
	if (Array.isArray(value)) return `(${value.map(toODataValue).join(", ")})`;
	throw new Error("Unknown type.");
}
function compareValue(a, b) {
	if (a == null) return b == null ? 0 : -1;
	if (typeof a !== typeof b) return 0;
	if (typeof a == "number") return a - b;
	if (typeof a == "string") return a.localeCompare(b);
	if (typeof a == "boolean") {
		if (a === b) return 0;
		return a ? 1 : -1;
	}
	if (a instanceof Date) return a.getTime() - b.getTime();
	return 0;
}
/**
* Checks if the specified values are equivalents.
*
* @param value1 - first value
* @param value2 - second value
* @returns A value indicating whether the specified values are equivalents
*/
function isEquivalentValues(value1, value2) {
	if (value1 == null) return value2 == null;
	if (value2 == null) return value1 == null;
	if (value1 === value2) return true;
	if (typeof value1 === "object" && typeof value2 === "object") return JSON.stringify(value1) === JSON.stringify(value2);
	return false;
}
function isObject(value) {
	return typeof value === "object" && value !== null;
}
//#endregion
//#region src/helpers/random.ts
var Random = class {
	constructor(seed) {
		if (seed === void 0) this.seed = Date.now();
		else this.seed = seed;
	}
	next() {
		const x = Math.sin(this.seed++) * 1e4;
		return x - Math.floor(x);
	}
	nextInt(maxValue) {
		return Math.floor(this.next() * maxValue);
	}
	nextDouble() {
		return this.next();
	}
	nextBytes(buffer) {
		for (let i = 0; i < buffer.length; i++) buffer[i] = this.nextInt(256);
	}
	nextRange(minValue, maxValue) {
		return Math.floor(this.next() * (maxValue - minValue) + minValue);
	}
};
//#endregion
//#region src/helpers/regex.ts
/**
* Provides enumerated values to use to set regular expression options.
* This enumeration has a FlagsAttribute attribute that allows a bitwise combination of its member values.
*/
var RegexOptions = /* @__PURE__ */ function(RegexOptions) {
	RegexOptions[RegexOptions["None"] = 0] = "None";
	RegexOptions[RegexOptions["IgnoreCase"] = 1] = "IgnoreCase";
	RegexOptions[RegexOptions["Multiline"] = 2] = "Multiline";
	RegexOptions[RegexOptions["ExplicitCapture"] = 4] = "ExplicitCapture";
	RegexOptions[RegexOptions["Compiled"] = 8] = "Compiled";
	RegexOptions[RegexOptions["Singleline"] = 16] = "Singleline";
	RegexOptions[RegexOptions["IgnorePatternWhitespace"] = 32] = "IgnorePatternWhitespace";
	RegexOptions[RegexOptions["RightToLeft"] = 64] = "RightToLeft";
	RegexOptions[RegexOptions["ECMAScript"] = 256] = "ECMAScript";
	RegexOptions[RegexOptions["CultureInvariant"] = 512] = "CultureInvariant";
	return RegexOptions;
}({});
/**
* Represents an immutable regular expression.
*/
var Regex = class Regex {
	/**
	* Underlying javascript RegExp instance.
	*
	* @readonly
	*/
	get regex() {
		return new RegExp(this.pattern, Regex.getFlag(this.options));
	}
	/**
	* Initializes a new instance of the Regex class for the specified regular expression, with options that modify the pattern.
	* @param pattern - The regular expression pattern to match.
	* @param options - A bitwise combination of the enumeration values that provide options for matching (default: RegexOptions.None).
	*/
	constructor(pattern, options = 0) {
		this.pattern = pattern;
		this.options = options;
	}
	/**
	* Indicates whether the regular expression finds a match in the input string.
	*
	* @param input - The string to search for a match.
	* @param startat - The character position at which to start the search.
	* @returns true if the regular expression finds a match; otherwise, false.
	*/
	isMatch(input, startat = 0) {
		return input.slice(startat).search(this.regex) >= 0;
	}
	/**
	* Indicates whether the specified regular expression finds a match in the specified input string, using the specified matching options.
	*
	* @param input - The string to search for a match.
	* @param pattern - The regular expression pattern to match.
	* @param options - A bitwise combination of the enumeration values that provide options for matching (default: RegexOptions.None).
	* @returns true if the regular expression finds a match; otherwise, false.
	*/
	static isMatch(input, pattern, options = 0) {
		return new RegExp(pattern, Regex.getFlag(options)).test(input);
	}
	/**
	* In a specified input substring, replaces a specified maximum number of strings that match a regular expression pattern
	* with a specified replacement string.
	*
	* @param input - The string to search for a match.
	* @param replacement - The replacement string.
	* @param count - Maximum number of times the replacement can occur (default: -1). If negative, the input will be fully replaced.
	* @param startat - The character position in the input string where the search begins (default: 0).
	* @returns A new string that is identical to the input string, except that the replacement string takes the place of each matched string.
	* If the regular expression pattern is not matched in the current instance, the method returns the current instance unchanged.
	*/
	replace(input, replacement, count = -1, startat = 0) {
		let res = input.slice(startat);
		const prefix = input.slice(0, startat);
		if (count < 0) return `${prefix}${res.replace(this.regex, replacement)}`;
		const rx = new RegExp(this.pattern, Regex.getFlag(this.options, false));
		for (let i = count; i > 0; i--) res = res.replace(rx, replacement);
		return `${prefix}${res}`;
	}
	/**
	* In a specified input string, replaces all strings that match a specified regular expression with a specified replacement string.
	* Specified options modify the matching operation.
	*
	* @param input - The string to search for a match.
	* @param pattern - The regular expression pattern to match.
	* @param replacement - The replacement string.
	* @param options - A bitwise combination of the enumeration values that provide options for matching (default: RegexOptions.None).
	* @returns A new string that is identical to the input string, except that the replacement string takes the place of each matched string.
	* If pattern is not matched in the current instance, the method returns the current instance unchanged.
	*/
	static replace(input, pattern, replacement, options = 0) {
		return new Regex(pattern, options).replace(input, replacement);
	}
	/**
	* Splits an input string a specified maximum number of times into an array of substrings,
	* at the positions defined by a regular expression specified in the Regex constructor.
	* The search for the regular expression pattern starts at a specified character position in the input string.
	*
	* @param input - The string to be split.
	* @param count - Maximum number of times the split can occur (default: -1). If negative, the input will be fully splitted.
	* @param startat - The character position in the input string where the search begins (default: 0).
	* @returns An array of strings.
	*/
	split(input, count = -1, startat = 0) {
		if (count === 1) return [input];
		const prefix = input.slice(0, startat);
		const partialInput = input.slice(startat);
		let result = [];
		if (count <= 0) result = partialInput.split(this.regex);
		else {
			const splitRegex = new RegExp(`(${this.pattern})`, Regex.getFlag(this.options));
			const fullSplitResult = partialInput.split(splitRegex);
			for (let i = 1; i < count; i++) {
				result.push(fullSplitResult[0]);
				fullSplitResult.shift();
				fullSplitResult.shift();
			}
			result.push(fullSplitResult.join(""));
		}
		result[0] = `${prefix}${result[0]}`;
		return result;
	}
	/**
	* Splits an input string into an array of substrings at the positions defined by a specified regular expression pattern.
	* Specified options modify the matching operation.
	*
	* @param input - The string to split.
	* @param pattern - The regular expression pattern to match.
	* @param options - A bitwise combination of the enumeration values that provide options for matching (default: RegexOptions.None).
	* @returns An array of strings.
	*/
	static split(input, pattern, options = 0) {
		return new Regex(pattern, options).split(input);
	}
	/**
	* Returns the javascript RegExp flags string that contains the flags to add.
	*
	* @param options - A bitwise combination of the enumeration values that provide options for matching.
	* @param global - If true, add the 'global' flag to the string corresponding to the C# defautl behaviour (default: true).
	* @returns A string containing the javascript RegExp flags to pass to the RegExp constructor.
	*/
	static getFlag(options, global = true) {
		let flag = global ? "g" : "";
		if ((options & 1) === 1) flag = `${flag}i`;
		if ((options & 2) === 2) flag = `${flag}m`;
		if ((options & 16) === 16) flag = `${flag}s`;
		return flag;
	}
};
/**
* Escape javascript regex string with special characters (see : https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping)
* @param string - The regex string to escape.
* @returns - A string with escaped special regex characters.
*/
function escapeRegExp(string) {
	return string.replaceAll(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
//#endregion
//#region src/helpers/registry.ts
function createRegistry(type) {
	const instances = /* @__PURE__ */ new Map();
	function register(name, instance) {
		instances.set(name, instance);
	}
	function find(name) {
		return instances.get(name);
	}
	function get(name) {
		const instance = find(name);
		if (!instance) throw new Error(`${type} with name "${name}" is not registered.`);
		return instance;
	}
	return {
		instances,
		register,
		find,
		get
	};
}
function createRegistryWithDefault(type) {
	const registry = createRegistry(type);
	function register(name, instance) {
		if (registry.instances.size === 0) registry.instances.set(void 0, instance);
		registry.instances.set(name, instance);
	}
	return {
		...registry,
		register
	};
}
//#endregion
//#region src/helpers/stringBuilder.ts
var StringBuilder = class {
	/**
	* Initializes a new instance of the StringBuilder class.
	* @param value - The string used to initialize the value of the instance.
	*/
	constructor(value = "") {
		this.value = value;
	}
	/**
	* Appends a value.
	* @param value - The value to append.
	* @returns the instance.
	*/
	append(value) {
		this.value += value;
		return this;
	}
	/**
	* Replaces the value.
	* @param value - The new value.
	* @returns the instance.
	*/
	replace(value) {
		this.value = value;
		return this;
	}
	/**
	* Gets the value.
	* @returns the value.
	*/
	toString() {
		return this.value;
	}
};
//#endregion
//#region src/helpers/timespan.ts
/**
* Regex for parsing TimeSpan format: [d.]hh:mm:ss[.fffffff]
* Examples: "1.02:03:04.5678901", "02:03:04", "02:03:04.567", "-1.02:03:04"
*/
var timeSpanRegex = /^(?<sign>-)?(?:(?<days>\d+)\.)?(?<hours>\d{1,2}):(?<minutes>\d{2}):(?<seconds>\d{2})(?:\.(?<fraction>\d{1,7}))?$/;
/**
* Converts the string representation of a time interval to its Duration equivalent.
* @param value - A string containing a time interval to convert.
* @param result - A Duration equivalent to the string value or Duration.fromMillis(0) if value is not valid.
* @returns A value indicating whether the string has been parsed to a Duration with success.
*/
function tryParseDuration(value, result) {
	const match = timeSpanRegex.exec(value);
	if (!match?.groups) {
		result.value = Duration.fromMillis(0);
		return false;
	}
	const sign = match.groups.sign === "-" ? -1 : 1;
	const days = Number.parseInt(match.groups.days || "0", 10);
	const hours = Number.parseInt(match.groups.hours, 10);
	const minutes = Number.parseInt(match.groups.minutes, 10);
	const seconds = Number.parseInt(match.groups.seconds, 10);
	let milliseconds = 0;
	if (match.groups.fraction) {
		const fraction = match.groups.fraction.padEnd(7, "0");
		milliseconds = Math.round(Number.parseInt(fraction, 10) / 1e4);
	}
	if (hours > 23 || minutes > 59 || seconds > 59) {
		result.value = Duration.fromMillis(0);
		return false;
	}
	const totalMilliseconds = sign * (days * 24 * 60 * 60 * 1e3 + hours * 60 * 60 * 1e3 + minutes * 60 * 1e3 + seconds * 1e3 + milliseconds);
	result.value = Duration.fromMillis(totalMilliseconds);
	return true;
}
//#endregion
//#region src/helpers/timeZoneInfo.ts
var TimeZoneInfo = class TimeZoneInfo {
	static {
		this.local = new TimeZoneInfo(Intl.DateTimeFormat().resolvedOptions().timeZone);
	}
	/**
	* Initializes a new instance of the TimeZoneInfo class.
	* @param id - The time zone identifier, which corresponds to the Id property.
	*/
	constructor(id) {
		this.id = id;
	}
	/**
	* Converts a given UTC date and time to the specified destination time zone.
	*
	* @param dateTime - The UTC date and time to convert (the UTC date must be stored as a local date (this isn't obvious, but it allows the UTC date to be displayed correctly when shown in an input).
	* @param destinationTimeZone - The target time zone information for conversion.
	* @returns A new `Date` object representing the local time in the destination time zone.
	*/
	static convertTimeFromUtc(dateTime, destinationTimeZone) {
		const destinationTimezoneOffset = TimeZoneInfo.getTimezoneOffset(dateTime, destinationTimeZone);
		const utc = dateTime.getTime() + destinationTimezoneOffset * 6e4;
		return new Date(utc);
	}
	/**
	* Converts a given date and time from a specified source time zone to its equivalent UTC time.
	*
	* @param dateTime - The local date and time to be converted.
	* @param sourceTimeZone - The time zone information representing the source time zone of the input dateTime.
	* @returns A new `Date` object representing the equivalent UTC time (the UTC date is stored as a local date (this isn't obvious, but it allows the UTC date to be displayed correctly when shown in an input).
	*/
	static convertTimeToUtc(dateTime, sourceTimeZone) {
		const sourceTimezoneOffset = TimeZoneInfo.getTimezoneOffset(dateTime, sourceTimeZone);
		const utc = dateTime.getTime() - sourceTimezoneOffset * 6e4;
		return new Date(utc);
	}
	static getTimezoneOffset(atTime, timeZone) {
		const localizedTime = new Date(atTime.toLocaleString("en-US", { timeZone: timeZone.id }));
		const utcTime = new Date(atTime.toLocaleString("en-US", { timeZone: "UTC" }));
		return (localizedTime.getTime() - utcTime.getTime()) / 6e4;
	}
};
//#endregion
//#region src/helpers/uri.ts
var hexRegex = /^%[0-9A-Z]{2}/;
/**
* Converts a specified hexadecimal representation of a character to the character.
* @param pattern - The hexadecimal representation of a character.
* @param index - The location in pattern where the hexadecimal representation of a character begins.
* @returns - The character represented by the hexadecimal encoding at position index.
* If the character at index is not hexadecimal encoded, the character at index is returned.
* The value of index is incremented to point to the character following the one returned.
*/
function hexUnescape(pattern, index) {
	const startPattern = pattern.substring(index.value);
	if (!hexRegex.test(startPattern)) return startPattern.slice(0, 1);
	const decoded = decodeURI(startPattern);
	index.value += 3;
	return decoded.slice(0, 1);
}
//#endregion
//#region src/helpers/window.ts
/**
* Retrieves the window object of an iframe element with the specified name.
*
* @param name - The name attribute of the iframe element.
* @param root - The root element to search within, defaults to the document.
* @returns The window object of the iframe, or null if not found.
*/
function getIframeByName(name, root) {
	const iframe = (root ?? document).querySelector(`iframe[name='${name}']`);
	if (iframe) return iframe.contentWindow;
	return null;
}
//#endregion
//#region src/theme/ThemeManager.ts
var ThemeManager = class {
	constructor(themes, importTheme) {
		this.currentTheme = {
			name: "NeosV3",
			colorScheme: "light",
			animatedButtons: true,
			buttonsLayoutReverseOrder: false
		};
		this.changedEvent = createLocalEvent();
		this._valuesCache = /* @__PURE__ */ new Map();
		this.themes = themes;
		this._importTheme = importTheme;
	}
	async setThemeByNameAsync(themeName) {
		const themeInfo = this.themes.find((t) => t.name === themeName);
		if (!themeInfo) return false;
		const cssContent = await this._importTheme(themeName);
		if (!cssContent) return false;
		await this.setThemeAsync(themeInfo, cssContent);
		return true;
	}
	async setThemeAsync(theme, cssContent) {
		this.currentTheme = theme;
		const loadedThemeElements = Array.from(document.querySelectorAll(`[data-theme]`));
		let themeElement = loadedThemeElements.find((t) => t.getAttribute("data-theme") === theme.name);
		if (!themeElement) {
			themeElement = document.createElement("style");
			themeElement.setAttribute("type", "text/css");
			themeElement.setAttribute("data-theme", theme.name);
			document.head.append(themeElement);
		}
		themeElement.textContent = cssContent;
		for (const loadedThemeElement of loadedThemeElements) if (loadedThemeElement === themeElement) loadedThemeElement.removeAttribute("media");
		else loadedThemeElement.media = "max-width: 1px";
		this._valuesCache.clear();
		this.changedEvent.raise({
			...theme,
			cssContent
		});
	}
	getThemeValue(name, options) {
		let propertyValue = this._valuesCache.get(name);
		if (propertyValue === void 0) {
			propertyValue = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`);
			this._valuesCache.set(name, propertyValue);
		}
		if (propertyValue !== "") return options?.convert ? options.convert(propertyValue) : propertyValue;
		return options?.defaultValue;
	}
};
//#endregion
//#region src/theme/themeManagerRegistry.ts
function registerThemeManager(manager) {
	const global = globalThis;
	global.themeManager = manager;
}
function getThemeManager() {
	const global = globalThis;
	if (!global.themeManager) throw new Error("Theme manager is not registered");
	return global.themeManager;
}
//#endregion
//#region src/sanitizing/escapeHtml.ts
var escapeHtml = (unsafe) => {
	return unsafe.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll("\"", "&quot;").replaceAll("'", "&#039;").replaceAll("/", "&#x2F;");
};
//#endregion
//#region src/i18n/globalResourceManagerRegistry.ts
var registry$1 = createRegistryWithDefault("Global resource manager");
var registerGlobalResourceManager = registry$1.register.bind(registry$1);
var getGlobalResourceManager = registry$1.get.bind(registry$1);
//#endregion
//#region src/i18n/Localizator.ts
/**
* Localizator.
*/
var Localizator = class Localizator {
	constructor(languages) {
		this.languages = languages;
	}
	/**
	* Gets the default language.
	*/
	get defaultLanguage() {
		return this.languages[0];
	}
	/**
	* Gets the current language (IETF language tag).
	*/
	get currentLanguage() {
		const currentLanguage = this.options?.currentLanguage ?? this.defaultLanguage;
		if (!currentLanguage.includes("-") && !this.languages.includes(currentLanguage)) return this.defaultLanguage;
		return currentLanguage;
	}
	/**
	* Gets the current culture.
	*/
	get currentCulture() {
		return this.options?.currentCulture ?? this.defaultLanguage;
	}
	/**
	* Gets the languages of the localizable string.
	*/
	get localizableStringLanguages() {
		return this.options?.localizableStringLanguages ?? moveElementFirst([...this.languages], this.currentLanguage);
	}
	/**
	* Gets the default language of the localizable string.
	*/
	get localizableStringDefaultLanguage() {
		return this.options?.localizableStringDefaultLanguage ?? this.defaultLanguage;
	}
	/**
	* Gets the current language of the localizable string.
	*/
	get localizableStringCurrentLanguage() {
		return this.options?.localizableStringCurrentLanguage ?? this.localizableStringLanguages[0];
	}
	/**
	* Gets the required language of the localizable string.
	*/
	get localizableStringRequiredLanguage() {
		return this.options?.localizableStringRequiredLanguage ?? this.localizableStringLanguages[0];
	}
	init(options) {
		this.options = options;
	}
	/**
	* Translates an input according to the localizator options.
	* @param input - Input to translate, can be a localizable string object or a string.
	* @returns The translation.
	*/
	translate(input) {
		if (!input) return;
		if (typeof input === "string") return input;
		return input[this.currentLanguage] ?? input[this.defaultLanguage] ?? Localizator.getFirstTranslation(input);
	}
	/**
	* Translates a localizable string value according to the localizator options.
	* @param input - Localizable string value.
	* @returns The translation.
	*/
	translateLocalizableDefinedString(input) {
		return input[this.localizableStringCurrentLanguage] ?? input[this.localizableStringDefaultLanguage] ?? Localizator.getFirstTranslation(input);
	}
	/**
	* Translates a localizable string value according to the localizator options.
	* @param input - Localizable string value or undefined.
	* @returns The translation.
	*/
	translateLocalizableString(input) {
		return input ? this.translateLocalizableDefinedString(input) : void 0;
	}
	/**
	* Gets the first translation of a localizable string object.
	* @param input  - Localizable string value.
	* @returns - The first translation or undefined if localizable string has no translations.
	*/
	static getFirstTranslation(input) {
		return Object.keys(input).map((key) => input[key])[0];
	}
	/**
	* Gets a new instance with a modified translation.
	* @param localizable - Localizable.
	* @param language - Language to modify or add.
	* @param translation - Translation to set for the language.
	* @returns
	*/
	static withTranslation(localizable, language, translation) {
		return {
			...localizable,
			[language]: translation
		};
	}
};
//#endregion
//#region src/i18n/localizatorRegistry.ts
var registry = createRegistry("Localizator");
var registerLocalizator = registry.register.bind(registry);
var getLocalizator = registry.get.bind(registry);
//#endregion
//#region src/i18n/ResourceManager.ts
/**
* Resource manager.
*/
var ResourceManager = class ResourceManager {
	/**
	* Initialize a resource manager instance.
	* @param importResources - Importer.
	*/
	constructor(importResources) {
		this.resources = {};
		if (typeof importResources === "function") this.importResources = importResources;
		else this.resources = importResources;
	}
	/**
	* Load the resources.
	*/
	async load(localizator) {
		const load = ResourceManager.getImportResourceFn(localizator, this.importResources);
		if (load) this.resources = (await load()).default;
	}
	static getImportResourceFn(localizator, importResources) {
		let fn = importResources(localizator.currentCulture);
		fn ??= importResources(ResourceManager.getTwoLetterLanguage(localizator.currentLanguage));
		fn ??= importResources("en");
		return fn;
	}
	static getTwoLetterLanguage(language) {
		const separatorCharIndex = language.indexOf("-");
		if (separatorCharIndex !== -1) language = language.substring(0, separatorCharIndex);
		return language;
	}
	/**
	* Get a resource by a key.
	*
	* @param key - The key.
	* @returns The resource of the specified key if it exists, otherwise the key.
	*/
	get(key) {
		return this.find(key) ?? key;
	}
	/**
	* Find a resource by a key.
	*
	* @param key - The key.
	* @returns The resource of the specified key if it exists, otherwise undefined.
	*/
	find(key) {
		return getPropertyByPath(this.resources, key);
	}
};
/**
* Replaces the format item in a specified string with the string representation of a corresponding object in a specified array.
*
* @param fmt - A composite format string.
* @param args - An object array that contains zero or more objects to format.
* @returns string.
*/
function stringFormat(fmt, ...args) {
	return fmt.replaceAll(/\{\{|\}\}|\{([A-Za-z0-9-]+)\}/g, (substring, arg) => {
		switch (substring) {
			case "{{": return "{";
			case "}}": return "}";
			default: {
				const id = Number.parseInt(arg, 10);
				if (Number.isNaN(id)) {
					if (args.length === 0 || typeof args[0] !== "object") throw new Error("Format function must contain an object type argument.");
					return args[0][arg];
				}
				if (id >= args.length) throw new Error(`'${id}' argument is out of range.`);
				return args[id];
			}
		}
	});
}
//#endregion
//#region src/log/application-insights/applicationInsights.ts
var applicationInsights;
/**
* Gets the Application Insights instance.
*/
function getApplicationInsights() {
	return applicationInsights;
}
/**
* Initializes Application Insights and returns its configuration.
*/
async function initializeApplicationInsights() {
	try {
		const response = await fetch(`${getBaseUrl()}/applicationInsights.json`, { headers: { Accept: "application/json" } });
		if (response.ok) {
			const settings = await response.json();
			if (Object.getOwnPropertyNames(settings).length > 0) {
				const { createApplicationInsightsInstance } = await import("./createApplicationInsightsInstance-Csf0DDai.js");
				applicationInsights = createApplicationInsightsInstance(settings);
				return settings;
			}
		}
	} catch (error) {
		console.error("Failed to initialize Application Insights", error);
	}
	applicationInsights = void 0;
}
//#endregion
//#region src/log/ConsoleLogger.ts
/**
* Represents a console logger.
*/
var ConsoleLogger = class {
	constructor(options) {
		this.name = options?.name;
		this.isDebugEnabled = options?.isDebugEnabled ?? false;
	}
	/**
	* Sends a log message as a trace to Application Insights.
	* @param severityLevel - Severity level.
	* @param message - Message.
	*/
	logMessage(severityLevel, message) {
		if (severityLevel === SeverityLevel.Warning || severityLevel === SeverityLevel.Error || severityLevel === SeverityLevel.Critical) getApplicationInsights()?.trackTrace({
			message,
			severityLevel
		});
	}
	/** {@inheritdoc Logger.trace} */
	trace(message, ...parameters) {
		if (this.isDebugEnabled) {
			message = this.enrichMessage(message, ...parameters);
			this.logMessage(SeverityLevel.Verbose, message);
			this.writeToConsole(window.console.trace, message, parameters);
		}
	}
	/** {@inheritdoc Logger.debug} */
	debug(message, ...parameters) {
		if (this.isDebugEnabled) {
			message = this.enrichMessage(message, ...parameters);
			this.logMessage(SeverityLevel.Verbose, message);
			this.writeToConsole(window.console.debug, message, parameters);
		}
	}
	/** {@inheritdoc Logger.info} */
	info(message, ...parameters) {
		message = this.enrichMessage(message, ...parameters);
		this.logMessage(SeverityLevel.Information, message);
		this.writeToConsole(window.console.info, message, parameters);
	}
	/** {@inheritdoc Logger.warn} */
	warn(message, ...parameters) {
		message = this.enrichMessage(message, ...parameters);
		this.logMessage(SeverityLevel.Warning, message);
		this.writeToConsole(window.console.warn, message, parameters);
	}
	/** {@inheritdoc Logger.error} */
	error(message, ...parameters) {
		message = this.enrichMessage(message, ...parameters);
		this.logMessage(SeverityLevel.Error, message);
		this.writeToConsole(window.console.error, message, parameters);
	}
	/** {@inheritdoc Logger.errorException} */
	errorException(err) {
		const message = this.enrichMessage(err.message);
		this.logMessage(SeverityLevel.Error, message);
		window.console.error(err);
	}
	enrichMessage(message, ...parameters) {
		let logMessage = message;
		if (parameters.length > 0) try {
			logMessage = stringFormat(message, ...parameters);
		} catch {
			logMessage = message;
		}
		if (this.name) logMessage = `${this.name}: ${logMessage}`;
		return logMessage;
	}
	writeToConsole(writeFunction, message, params) {
		if (params.length > 0) writeFunction(message, ...params);
		else writeFunction(message);
	}
};
//#endregion
//#region src/shortcuts/ShortcutTrigger.ts
/**
* Represents a shortcut trigger.
*/
var ShortcutTrigger = class ShortcutTrigger {
	/**
	* Initializes a new instance of the ShortcutTrigger class.
	* @param key - The key.
	* @param ctrl - The value indicating whether the ctrl key must be pressed.
	* @param shift - The value indicating whether the shift key must be pressed.
	* @param alt - The value indicating whether the alt key must be pressed.
	*/
	constructor(key, ctrl = false, shift = false, alt = false) {
		this.key = key;
		this.ctrl = ctrl;
		this.shift = shift;
		this.alt = alt;
	}
	/**
	* Converts to string.
	* @returns String.
	*/
	toString() {
		let str = "";
		if (this.ctrl) str += "Ctrl+";
		if (this.shift) str += "Shift+";
		if (this.alt) str += "Alt+";
		str += this.key.toUpperCase();
		return str;
	}
	static parse(shortcut) {
		const parts = shortcut.toUpperCase().split("+").map((s) => s.trim());
		if (parts.length === 0 || parts.length > 4) return;
		const key = parts[parts.length - 1];
		if (!ShortcutTrigger.isValidKey(key)) return;
		if (parts.length === 1) return new ShortcutTrigger(key);
		const mods = new Set(parts.slice(0, -1));
		return new ShortcutTrigger(key, mods.has("CTRL"), mods.has("SHIFT"), mods.has("ALT"));
	}
	static isValid(key, ctrl, shift, alt) {
		if (key >= "F1" && key <= "F12") return true;
		if ((ctrl || shift || alt) && ShortcutTrigger.isValidKey(key.toUpperCase())) return true;
		return false;
	}
	static isValidKey(key) {
		return key !== "CONTROL" && key !== "SHIFT" && key !== "ALT";
	}
};
//#endregion
//#region src/summaries/SummaryType.ts
/**
* Specifies the summary type.
*/
var SummaryType = /* @__PURE__ */ function(SummaryType) {
	/**
	* Average.
	*/
	SummaryType["average"] = "average";
	/**
	* Values count.
	*/
	SummaryType["count"] = "count";
	/**
	* Distinct values count.
	*/
	SummaryType["countDistinct"] = "countDistinct";
	/**
	* Maximum.
	*/
	SummaryType["max"] = "max";
	/**
	* Minimum.
	*/
	SummaryType["min"] = "min";
	/**
	* Sum.
	*/
	SummaryType["sum"] = "sum";
	return SummaryType;
}({});
//#endregion
//#region src/summaries/SummaryTypes.ts
function createDefaultSummaryType(name) {
	return {
		_caption: void 0,
		_tag: void 0,
		get caption() {
			return this._caption !== void 0 ? this._caption : getGlobalResourceManager(void 0).get(`SummaryType_${name}_Caption`);
		},
		set caption(value) {
			this._caption = value;
		},
		get tag() {
			return this._tag !== void 0 ? this._tag : getGlobalResourceManager(void 0).get(`SummaryType_${name}_Tag`);
		},
		set tag(value) {
			this._tag = value;
		}
	};
}
function createSummaryType(name) {
	const summaryType = summaryTypes[name];
	return {
		_caption: void 0,
		_tag: void 0,
		get caption() {
			return this._caption !== void 0 ? this._caption : summaryType.caption;
		},
		set caption(value) {
			this._caption = value;
		},
		get tag() {
			return this._tag !== void 0 ? this._tag : summaryType.tag;
		},
		set tag(value) {
			this._tag = value;
		}
	};
}
var summaryTypes = {
	average: createDefaultSummaryType("Average"),
	count: createDefaultSummaryType("Count"),
	countDistinct: createDefaultSummaryType("CountDistinct"),
	max: createDefaultSummaryType("Max"),
	min: createDefaultSummaryType("Min"),
	sum: createDefaultSummaryType("Sum")
};
function createSummaryTypes() {
	return {
		average: createSummaryType(SummaryType.average),
		count: createSummaryType(SummaryType.count),
		countDistinct: createSummaryType(SummaryType.countDistinct),
		max: createSummaryType(SummaryType.max),
		min: createSummaryType(SummaryType.min),
		sum: createSummaryType(SummaryType.sum)
	};
}
//#endregion
//#region src/tasks/TaskStatus.ts
/**
* Represents the current stage in the lifecycle of a Task.
*/
var TaskStatus = /* @__PURE__ */ function(TaskStatus) {
	/**
	* The task acknowledged cancellation.
	*/
	TaskStatus[TaskStatus["canceled"] = 6] = "canceled";
	/**
	* The task has been initialized but has not yet been scheduled.
	*/
	TaskStatus[TaskStatus["created"] = 0] = "created";
	/**
	* The task completed due to an unhandled exception.
	*/
	TaskStatus[TaskStatus["faulted"] = 7] = "faulted";
	/**
	* The task completed execution successfully.
	*/
	TaskStatus[TaskStatus["ranToCompletion"] = 5] = "ranToCompletion";
	/**
	* The task is running but has not yet completed.
	*/
	TaskStatus[TaskStatus["running"] = 3] = "running";
	return TaskStatus;
}({});
//#endregion
//#region src/tasks/Task.ts
/**
* Represents a task that manages the execution of a Promise.
*/
var Task = class Task extends Promise {
	/**
	* Initializes a new instance of the Task class.
	* @param func - Function that must be called by the task.
	* @param state - An object representing data to be used by the function.
	*/
	constructor(executor, properties) {
		super(executor);
		this.properties = properties;
	}
	/**
	* Gets a task that has already completed successfully.
	*/
	static get completedTask() {
		const task = Task.create(() => {});
		task.startTask();
		task.properties.status = TaskStatus.ranToCompletion;
		return task;
	}
	/**
	* Gets the state object supplied when the Task was created, or null if none was supplied.
	*/
	get asyncState() {
		return this.properties.asyncState;
	}
	/**
	* Gets an exception that caused the Task to end prematurely.
	* If the Task completed successfully or has not yet thrown any exceptions, this will return undefined.
	*/
	get exception() {
		return this.properties.exception;
	}
	/**
	* Gets whether this Task instance has completed execution due to being canceled.
	*/
	get isCanceled() {
		return this.status === TaskStatus.canceled;
	}
	/**
	* Gets a value that indicates whether the task has completed.
	*/
	get isCompleted() {
		return this.isCanceled || this.isCompletedSuccessfully || this.isFaulted;
	}
	/**
	* Gets whether the task ran to completion.
	*/
	get isCompletedSuccessfully() {
		return this.status === TaskStatus.ranToCompletion;
	}
	/**
	* Gets whether the Task completed due to an unhandled exception.
	*/
	get isFaulted() {
		return this.status === TaskStatus.faulted;
	}
	/**
	* Gets the result value of this Task.
	*/
	get result() {
		if (!this.isCompleted) throw new Error("Cannot read the result of an uncompleted task.");
		else if (!this.isCompletedSuccessfully) throw new Error("Cannot read the result of a faulted task.");
		else return this.properties.result;
	}
	/**
	* Gets the TaskStatus of this task
	*/
	get status() {
		return this.properties.status;
	}
	/**
	* Creates a continuation that executes asynchronously when the target promise completes.
	* @param promise - The promise on which the continuation is to be applied.
	* @param callback - The callback called when the promise is resolved or rejected.
	* @param state - An optional value that is accessible in the callback.
	*/
	static continueWith(promise, callback, state) {
		const executor = () => {};
		promise.then((v) => {
			callback(new Task(executor, {
				result: v,
				isContinuationTask: true,
				parentHasFailed: false,
				status: TaskStatus.ranToCompletion,
				asyncState: state
			}));
		}).catch((error) => {
			callback(new Task(executor, {
				isContinuationTask: true,
				parentHasFailed: true,
				exception: error,
				status: TaskStatus.faulted,
				asyncState: state
			}));
		});
	}
	/**
	* Creates a task.
	* @param func - Function that must be called by the task.
	* @param state - An object representing data to be used by the function.
	* @returns Task.
	*/
	static create(func, state) {
		const properties = {
			asyncState: state,
			isContinuationTask: false,
			parentHasFailed: false,
			status: TaskStatus.created
		};
		const executor = (resolve, reject) => {
			properties.func = async () => {
				try {
					if (properties.parentHasFailed) throw properties.exception;
					properties.status = TaskStatus.running;
					properties.result = await func(properties.asyncState);
					properties.status = TaskStatus.ranToCompletion;
					resolve(properties.result);
				} catch (error) {
					properties.exception = error;
					properties.status = TaskStatus.faulted;
					if (properties.continuationTask !== void 0) {
						properties.continuationTask.properties.exception = error;
						properties.continuationTask.properties.parentHasFailed = true;
					}
					reject(error);
				} finally {
					if (properties.continuationTask !== void 0) properties.continuationTask.startTask();
				}
			};
		};
		return new Task(executor, properties);
	}
	/**
	* Starts a task that completes after a specified number of milliseconds.
	* @param millisecondsDelay - The number of milliseconds to wait before completing the returned task, or -1 to wait indefinitely.
	*/
	static delay(millisecondsDelay) {
		return Task.run(() => {
			return new Promise((resolve) => {
				setTimeout(() => resolve(), millisecondsDelay);
			});
		});
	}
	/**
	* Creates a Task that has completed with a specified exception.
	* @param exception - Exception.
	* @returns Faulted task.
	*/
	static fromException(exception) {
		const task = Task.create(
			/* istanbul ignore next -- @preserve */
			() => {
				return {};
			}
		);
		task.properties.status = TaskStatus.faulted;
		task.properties.exception = exception;
		return task;
	}
	/**
	* Creates a Task<T> that's completed successfully with the specified result.
	* @param result - Result.
	* @returns Successfully completed task.
	*/
	static fromResult(result) {
		const task = Task.create(() => result);
		task.startTask();
		task.properties.status = TaskStatus.ranToCompletion;
		task.properties.result = result;
		return task;
	}
	/**
	* Starts a task that executes a function or a task.
	* @param funcOrTask - Function or task to be started.
	* @returns Started task.
	*/
	static run(funcOrTask) {
		let task;
		if (funcOrTask instanceof Task) task = funcOrTask;
		else task = Task.create(funcOrTask);
		task.start();
		return task;
	}
	/**
	* Waits for all of the provided Task objects to complete execution.
	* @param tasks - Tasks.
	*/
	static waitAll(tasks) {
		return Task.run(async () => {
			await Promise.allSettled(tasks);
		});
	}
	/**
	* Waits for any of the provided Task objects to complete execution.
	* @param tasks - Tasks.
	* @returns The index of the completed Task object in the tasks array.
	*/
	static waitAny(tasks) {
		return Task.run(() => {
			const tasksToExecute = [];
			for (let i = 0; i < tasks.length; i++) tasksToExecute.push(tasks[i].then(() => i).catch(() => i));
			return Promise.race(tasksToExecute);
		});
	}
	/**
	* Creates a task that will complete when all of the supplied tasks have completed.
	* @param tasks - Tasks.
	* @returns A task that represents the completion of all of the supplied tasks.
	*/
	static whenAll(tasks) {
		return Task.run(async () => {
			await Promise.allSettled(tasks);
			Task.aggregateExceptions(tasks);
		});
	}
	/**
	* Creates a task that will complete when all of the Task<T> objects in an enumerable collection have completed.
	* @param tasks - Tasks.
	* @returns A task that represents the completion of all of the supplied tasks.
	*/
	static whenAllResult(tasks) {
		return Task.run(async () => {
			const results = [];
			for (const task of tasks) task.then((v) => results.push(v)).catch((error) => results.push(error));
			await Promise.allSettled(tasks);
			Task.aggregateExceptions(tasks);
			return results;
		});
	}
	/**
	* Creates a task that will complete when any of the supplied tasks have completed.
	* @param tasks - Tasks.
	* @returns A task that represents the completion of one of the supplied tasks. The return task's Result is the task that completed.
	*/
	static whenAny(tasks) {
		return Task.run(async () => {
			const tasksToExecute = [];
			for (let i = 0; i < tasks.length; i++) tasksToExecute.push(tasks[i].then(() => i).catch(() => i));
			return tasks[await Promise.race(tasksToExecute)];
		});
	}
	/**
	* Aggregate the exceptions from a tasks collection.
	* @param tasks - Tasks collection.
	*/
	static aggregateExceptions(tasks) {
		if (tasks.some((t) => t.status === TaskStatus.faulted)) {
			const exceptions = [];
			for (const task of tasks.filter((t) => t.status === TaskStatus.faulted)) exceptions.push(task.exception);
			throw { innerExceptions: exceptions };
		}
	}
	/**
	* Creates a continuation task that will be executed after the task.
	* @param func - Function that will be by the continuation task.
	* @param state - An object representing data to be used by the function.
	* @returns Continuation task.
	*/
	continueWith(func, state) {
		const continuationTask = Task.create(() => func(this), state);
		continuationTask.properties.isContinuationTask = true;
		if (this.isCompletedSuccessfully) continuationTask.startTask();
		else this.properties.continuationTask = continuationTask;
		return continuationTask;
	}
	/**
	* Starts a task.
	*/
	start() {
		if (this.properties.isContinuationTask) throw new Error("Cannot start a continuation task.");
		this.startTask();
	}
	/**
	* Starts a task.
	*/
	startTask() {
		if (this.isCompleted) throw new Error("Cannot start a completed task.");
		else if (this.status === TaskStatus.running) throw new Error("Cannot start an already started task.");
		else {
			const func = this.properties.func;
			func();
		}
	}
};
/** Promise extensions  */
Object.defineProperty(Promise.prototype, "result", {
	get() {
		throw new Error("You cannot use Task.Result because the main thread must not be blocked.");
	},
	configurable: true
});
//#endregion
//#region src/navigator/Device.ts
var ResponsiveBreakpoint = /* @__PURE__ */ function(ResponsiveBreakpoint) {
	ResponsiveBreakpoint["ExtraSmall"] = "extrasmall";
	ResponsiveBreakpoint["Small"] = "small";
	ResponsiveBreakpoint["Medium"] = "medium";
	ResponsiveBreakpoint["Large"] = "large";
	ResponsiveBreakpoint["ExtraLarge"] = "extralarge";
	return ResponsiveBreakpoint;
}({});
var Device = class {
	constructor() {
		this.responsiveBreakpoint = getResponsiveBreakpoint(window.innerWidth);
	}
	/**
	* Gets a value indicating whether the device has a small screen width.
	*/
	get hasSmallScreenWidth() {
		return this.responsiveBreakpoint === "extrasmall" || this.responsiveBreakpoint === "small";
	}
	/**
	* Gets a value indicating whether the device has a large screen width.
	*/
	get hasLargeScreenWidth() {
		return this.responsiveBreakpoint === "medium" || this.responsiveBreakpoint === "large" || this.responsiveBreakpoint === "extralarge";
	}
	on() {
		this._resizeHandler = () => {
			this.responsiveBreakpoint = getResponsiveBreakpoint(window.innerWidth);
		};
		window.addEventListener("resize", this._resizeHandler);
	}
	off() {
		if (this._resizeHandler) {
			window.removeEventListener("resize", this._resizeHandler);
			this._resizeHandler = void 0;
		}
	}
};
function getResponsiveBreakpoint(width) {
	if (width >= 1280) return "extralarge";
	if (width >= 1024) return "large";
	if (width >= 768) return "medium";
	if (width >= 640) return "small";
	return "extrasmall";
}
//#endregion
//#region src/navigator/navigator.ts
function isDarkTheme() {
	return !!window.matchMedia && window.matchMedia("(prefers-color-scheme:dark)").matches;
}
//#endregion
export { CharacterCasing, ConsoleLogger, DataType, DateTimeFormatOption, DateTimeKind, Device, EventManager, GroupedArray, InvariantDateTime, Localizator, MidpointRounding, OpenModeType, PopupPosition, PopupSize, Random, Regex, RegexOptions, ResourceManager, ResponsiveBreakpoint, ShortcutTrigger, StringBuilder, StringComparison, SummaryType, Task, ThemeManager, TimeZoneInfo, WhiteSpaceHandling, addDays, addDaysToDateOnly, addMonthsToDateOnly, addToDate, addToDateOnly, addYearsToDateOnly, cast, compare, compareArray, compareDate, compareDateOnly, compareDictionaries, compareFromIndex, compareOrdinal, compareOrdinalFromIndex, compareRecords, compareValue, concat, contains, convertToOtherEnum, createDateOnly, createDateTime, createGuid, createLocalEvent, createRegistry, createRegistryWithDefault, createSummaryTypes, createTimeOnly, dateOnlyRegex, dateOnlyToDateTime, dateOnlyToShortDateString, dateOnlyToString, dateTimeRegex, dateTimeToDateOnly, dateTimeToTimeOnly, dateTimeWithOptionalTimezoneRegex, defaultDatetime, defaultFormatDateOnly, defaultFormatDateTime, defaultJsonReviver, emptyGuid, emptyObject, endOf, endOfDateOnly, endsWith, equals, escapeHtml, escapeRegExp, findLastIndex, formatDateTime, formatNum, fromRecord, getApplicationInsights, getArrayElementAt, getArrayElementAtOrDefault, getArraySearchFunction, getBaseUrl, getClusterBaseUrl, getDataTypeDefaultFormat, getDataTypeIcon, getDefaultArrayIfEmpty, getDefaultValueByType, getEnumValue, getEventManager, getFirstArrayElement, getFirstArrayElementOrDefault, getGlobalResourceManager, getIframeByName, getLastArrayElement, getLastArrayElementOrDefault, getLocalizator, getNumberArrayAverage, getNumberArraySum, getPropertyByPath, getQueryParameters, getSimplifiedDateFormat, getSingleArrayElement, getSingleArrayElementOrDefault, getThemeManager, groupBy, hasNoLeadingAndTrailingWhiteSpace, hasNoLeadingWhiteSpace, hasNoTrailingWhiteSpace, hasNoWhiteSpace, hexUnescape, indexOf, indexOfFromIndex, indexOfFromIndexAndLength, initialize, initializeApplicationInsights, initializeDefaultLocale, invariantDateTimeRegex, isDarkTheme, isDateDataType, isEnumDataType, isEquivalentValues, isGuid, isNullArray, isNullOrEmpty, isNullOrWhiteSpace, isObject, isWhiteSpace, lastIndexOf, lastIndexOfFromIndex, lastIndexOfFromIndexAndLength, moveElementFirst, openPath, parseDateTime, parseSimplifiedDate, parseTimeOnly, postMessageToVsCode, registerEventManager, registerGlobalResourceManager, registerLocalizator, registerThemeManager, relativeToAbsoluteUrl, remove, removeAll, removeAt, replace, replaceAll, replaceLocationPath, resetBaseUrl, resetVsCodeState, round, sameValue, setBaseUrl, setPropertyByPath, setTimeOfDay, setViewType, startOf, startOfDateOnly, startsWith, stringFormat, stripLastUrlSegment, summaryTypes, toCamelCase, toCamelCaseKey, toFourDigitYear, toIso, toLocalIso, toLowerCaseStart, toODataValue, toPascalCase, toRecord, trimChars, trimEndLines, tryCast, tryGetRecordValue, tryGetValue, tryParseDateTime, tryParseDuration, tryParseInteger, twoDigitYearMax, updateViewTypeWithId };

//# sourceMappingURL=shared.js.map